<script lang="ts">
  import { Link, router } from '@inertiajs/svelte';
  import { onDestroy } from 'svelte';
  import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
  import { toast } from 'svelte-sonner';
  import momentiumLogo from '../../assets/momentium-logo.png';
  import NavDropdown from '../../components/NavDropdown.svelte';
  import ProfileDropdown from '../../components/ProfileDropdown.svelte';
  import MobileTopBar from '../../components/mobile/MobileTopBar.svelte';
  import SegmentedControl from '../../components/mobile/SegmentedControl.svelte';
  import DateRangeNav from '../../components/DateRangeNav.svelte';
  import { tapAlternatives, viewport } from '../../lib/stores/viewport';
  import ActionSheet from '../../components/mobile/ActionSheet.svelte';
  import Icon from '../../components/Icon.svelte';
  import {
    moveItem,
    moveUpDownActions,
    type MoveDirection,
    type SheetAction,
  } from '../../lib/sheetActions';
  import { useModalAnimation } from '../../lib/useModalAnimation.svelte';
  import { jsonHeaders, readApiErrorMessage } from '../../lib/apiHelpers';
  import { getWeekStart, getWeekEnd } from '../../lib/utils';
  import type {
    CycleCadence,
    CycleCategory,
    CycleCategoryColor,
    CycleItem,
    CycleOccurrence,
    CycleOccurrenceAction,
    CycleNudgePreferences,
    CyclesPayload,
    CycleStatus,
  } from '../../lib/types';

  interface Props extends CyclesPayload {
    activeTab: 'current' | 'library' | 'history';
  }
  let {
    currentOccurrences,
    items,
    historyOccurrences,
    currentPeriods,
    currentPeriodEnds,
    categories,
    cycleNudgePreferences: initialNudgePreferences,
    activeTab: initialTab,
  }: Props = $props();

  const CADENCES: CycleCadence[] = ['weekly', 'monthly', 'quarterly', 'yearly'];
  const STATUS_LABELS: Record<CycleStatus, string> = {
    unstarted: 'Unstarted',
    in_progress: 'In progress',
    done: 'Done',
    skipped: 'Skipped',
    not_applicable: 'N/A',
  };
  const CADENCE_LABELS: Record<CycleCadence, string> = {
    weekly: 'Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
  };

  const CATEGORY_COLOR_ORDER: CycleCategoryColor[] = [
    'slate',
    'red',
    'orange',
    'amber',
    'green',
    'teal',
    'blue',
    'indigo',
    'purple',
    'pink',
  ];
  const CATEGORY_COLORS: Record<CycleCategoryColor, { text: string; bg: string; border: string }> =
    {
      slate: { text: '#475569', bg: '#f1f5f9', border: '#e2e8f0' },
      red: { text: '#b91c1c', bg: '#fee2e2', border: '#fecaca' },
      orange: { text: '#c2410c', bg: '#ffedd5', border: '#fed7aa' },
      amber: { text: '#b45309', bg: '#fef3c7', border: '#fde68a' },
      green: { text: '#166534', bg: '#dcfce7', border: '#bbf7d0' },
      teal: { text: '#0f766e', bg: '#ccfbf1', border: '#99f6e4' },
      blue: { text: '#1d4ed8', bg: '#dbeafe', border: '#bfdbfe' },
      indigo: { text: '#4338ca', bg: '#e0e7ff', border: '#c7d2fe' },
      purple: { text: '#6d28d9', bg: '#ede9fe', border: '#ddd6fe' },
      pink: { text: '#be185d', bg: '#fce7f3', border: '#fbcfe8' },
    };

  type ItemActionDraft = { id?: string; title: string };

  type ItemDraft = {
    title: string;
    category: string;
    cadence: CycleCadence;
    instructions: string;
    resource_url: string;
    estimated_duration: string;
    actions: ItemActionDraft[];
  };

  function blankDraft(cadence: CycleCadence = 'weekly'): ItemDraft {
    return {
      title: '',
      // Default to the user's first curated category rather than hardcoding
      // "General", which may have been renamed or removed from their list.
      category: localCategories[0]?.name ?? 'General',
      cadence,
      instructions: '',
      resource_url: '',
      estimated_duration: '',
      actions: [],
    };
  }

  // svelte-ignore state_referenced_locally -- Inertia props intentionally seed the view selected by its URL.
  let activeTab = $state<'current' | 'library' | 'history'>(initialTab);
  // svelte-ignore state_referenced_locally -- Inertia props seed independently mutable API state.
  let localCurrent = $state<CycleOccurrence[]>(currentOccurrences);
  // svelte-ignore state_referenced_locally -- Inertia props seed independently mutable API state.
  let localItems = $state<CycleItem[]>(items);
  // svelte-ignore state_referenced_locally -- Inertia props seed independently mutable API state.
  let localHistory = $state<CycleOccurrence[]>(historyOccurrences);
  // svelte-ignore state_referenced_locally -- Inertia props seed independently mutable API state.
  let localPeriods = $state<Record<CycleCadence, string>>(currentPeriods);
  // svelte-ignore state_referenced_locally -- Inertia props seed independently mutable API state.
  let localPeriodEnds = $state<Partial<Record<CycleCadence, string>>>(currentPeriodEnds ?? {});
  // svelte-ignore state_referenced_locally -- Inertia props seed independently mutable API state.
  let localCategories = $state<CycleCategory[]>(categories);
  const DEFAULT_NUDGE_PREFERENCES: CycleNudgePreferences = {
    enabled: true,
    highlight_items: true,
    thresholds: { weekly: 2, monthly: 5, quarterly: 10, yearly: 20 },
  };
  // svelte-ignore state_referenced_locally -- Inertia props seed independently mutable API state.
  let localNudgePreferences = $state<CycleNudgePreferences>({
    enabled: initialNudgePreferences?.enabled ?? DEFAULT_NUDGE_PREFERENCES.enabled,
    highlight_items:
      initialNudgePreferences?.highlight_items ?? DEFAULT_NUDGE_PREFERENCES.highlight_items,
    thresholds: {
      ...DEFAULT_NUDGE_PREFERENCES.thresholds,
      ...initialNudgePreferences?.thresholds,
    },
  });
  let nudgeSettingsOpen = $state(false);
  const nudgeModal = useModalAnimation(
    () => nudgeSettingsOpen,
    () => (nudgeSettingsOpen = false),
  );
  let nudgeDraft = $state<CycleNudgePreferences>({
    enabled: true,
    highlight_items: true,
    thresholds: { ...DEFAULT_NUDGE_PREFERENCES.thresholds },
  });
  let nudgeDraftBaseline = '';
  let nudgeDiscardConfirmation = $state(false);
  const nudgeDiscardModal = useModalAnimation(
    () => nudgeDiscardConfirmation,
    () => (nudgeDiscardConfirmation = false),
  );
  let nudgeDirty = $derived(nudgeSettingsOpen && JSON.stringify(nudgeDraft) !== nudgeDraftBaseline);
  let settingsMenuOpen = $state(false);
  let nudgeSaving = $state(false);
  let nudgeNow = $state(Date.now());
  let saving = $state(false);
  // === History grid (habits-style weekly checklist) ===
  // Columns are app weeks (Sunday-first), matching the weekly cadence period
  // keys. The current week is always the rightmost column, and navigation
  // shifts the whole window by the number of visible weeks.
  let showArchivedHistory = $state(false);
  // Rightmost visible week. Navigation is clamped to the current week so the
  // grid never extends into the future. Falls back to the current week.
  let historyEndWeek = $state<string | undefined>(undefined);
  type NotesPopover = { occurrence: CycleOccurrence; draft: string };
  let notesPopover = $state<NotesPopover | null>(null);
  let truncatedInstructions = $state<Record<string, boolean>>({});
  let expandedInstructions = $state<Record<string, boolean>>({});

  function instructionClampAction(
    node: HTMLElement,
    params: { occurrenceId: string; expanded: boolean },
  ) {
    let current = params;
    const check = () => {
      if (!current.expanded) {
        truncatedInstructions[current.occurrenceId] =
          node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1;
      }
    };
    check();
    const frame = requestAnimationFrame(check);
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(check);
    observer?.observe(node);
    return {
      update(next: { occurrenceId: string; expanded: boolean }) {
        current = next;
        requestAnimationFrame(check);
      },
      destroy() {
        cancelAnimationFrame(frame);
        observer?.disconnect();
      },
    };
  }

  function toggleInstruction(occurrenceId: string) {
    expandedInstructions[occurrenceId] = !expandedInstructions[occurrenceId];
  }
  let archiveConfirmation = $state<CycleItem | null>(null);
  const archiveModal = useModalAnimation(
    () => archiveConfirmation !== null,
    () => (archiveConfirmation = null),
  );
  let deleteConfirmation = $state<CycleItem | null>(null);
  const deleteModal = useModalAnimation(
    () => deleteConfirmation !== null,
    () => (deleteConfirmation = null),
  );

  // Modal-managed item form (used for both create and edit)
  type ItemFormState =
    | {
        mode: 'create';
        draft: ItemDraft;
      }
    | {
        mode: 'edit';
        item: CycleItem;
        draft: ItemDraft;
      };
  let itemForm = $state<ItemFormState | null>(null);
  // A plain string snapshot avoids reactive-proxy identity pitfalls while making
  // nested action edits part of the same unsaved-change check.
  let itemFormBaseline = '';
  let itemFormDirty = $derived(
    itemForm !== null && JSON.stringify(itemForm.draft) !== itemFormBaseline,
  );
  let itemDiscardConfirmation = $state(false);
  const itemModal = useModalAnimation(
    () => itemForm !== null,
    () => (itemForm = null),
  );
  const itemDiscardModal = useModalAnimation(
    () => itemDiscardConfirmation,
    () => (itemDiscardConfirmation = false),
  );

  // === Category picker (item form dropdown) ===
  let categoryMenuOpen = $state(false);
  let categoryMenuAnchor: HTMLElement | undefined = $state();
  let categoryMenuEl = $state<HTMLDivElement | undefined>();
  let categoryMenuStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');
  let categoryNewMode = $state(false);
  let categoryNewName = $state('');
  let categoryNewColor = $state<CycleCategoryColor>('slate');
  let categorySaving = $state(false);

  function categoryForName(name: string): CycleCategory | undefined {
    return localCategories.find((category) => category.name === name);
  }

  function categoryColorFor(name: string): CycleCategoryColor {
    return categoryForName(name)?.color ?? 'slate';
  }

  function tagStyle(name: string): string {
    const palette = CATEGORY_COLORS[categoryColorFor(name)];
    return `--cat-text:${palette.text};--cat-bg:${palette.bg};--cat-border:${palette.border};`;
  }

  function swatchStyle(color: CycleCategoryColor): string {
    return `background:${CATEGORY_COLORS[color].text};`;
  }

  function openCategoryMenu(anchor: HTMLElement) {
    categoryMenuStyle = 'position: fixed; left: 0; top: 0; visibility: hidden;';
    categoryNewMode = false;
    categoryNewName = '';
    categoryNewColor = 'slate';
    categoryMenuOpen = true;
    categoryMenuAnchor = anchor;
  }

  function closeCategoryMenu() {
    categoryMenuOpen = false;
    categoryMenuAnchor = undefined;
  }

  function pickCategory(category: CycleCategory) {
    if (itemForm) itemForm.draft.category = category.name;
    closeCategoryMenu();
  }

  async function createCategoryFromPicker() {
    if (categorySaving || !categoryNewName.trim()) return;
    categorySaving = true;
    try {
      const response = await fetch('/cycles/categories', {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({
          cycle_category: { name: categoryNewName.trim(), color: categoryNewColor },
        }),
      });
      if (!response.ok) {
        toast.error(await readApiErrorMessage(response, 'Could not add category.'));
        return;
      }
      const payload = (await response.json()) as CyclesPayload;
      applyPayload(payload);
      const created = payload.categories.find(
        (category) => category.name === categoryNewName.trim(),
      );
      if (created && itemForm) itemForm.draft.category = created.name;
      closeCategoryMenu();
    } catch {
      toast.error('Could not reach the server. Try again.');
    } finally {
      categorySaving = false;
    }
  }

  // === Manage categories modal ===
  let manageCategoriesOpen = $state(false);
  const manageCategoriesModal = useModalAnimation(
    () => manageCategoriesOpen,
    () => (manageCategoriesOpen = false),
  );
  let manageCategoryDrafts = $state<CycleCategory[]>([]);
  let newManageCategoryName = $state('');
  // null = no color picked yet (dotted circle); defaults to slate on save.
  let newManageCategoryColor = $state<CycleCategoryColor | null>(null);
  let manageCategorySaving = $state(false);
  let draggedCategoryId = $state<string | null>(null);
  let draggedLibraryItemId = $state<string | null>(null);
  let draggedActionIndex = $state<number | null>(null);
  let manageDeleteConfirmation = $state<CycleCategory | null>(null);
  const manageDeleteModal = useModalAnimation(
    () => manageDeleteConfirmation !== null,
    () => (manageDeleteConfirmation = null),
  );
  let manageDiscardConfirmation = $state(false);
  const manageDiscardModal = useModalAnimation(
    () => manageDiscardConfirmation,
    () => (manageDiscardConfirmation = false),
  );

  // Color picker popover shared by every manage row + the new-category row.
  let manageColorPopoverId = $state<string | null>(null);
  let manageColorAnchor: HTMLElement | undefined = $state();
  let manageColorEl = $state<HTMLDivElement | undefined>();
  let manageColorStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');
  let manageColorPopoverSelected = $derived.by(() => {
    if (manageColorPopoverId === null) return null;
    if (manageColorPopoverId === 'new') return newManageCategoryColor;
    return manageCategoryDrafts.find((entry) => entry.id === manageColorPopoverId)?.color ?? null;
  });

  // Custom color-name tooltip (app-style fixed-position hover tooltip; fixed so it
  // escapes the color popover's overflow clipping, like ProjectTooltip does).
  let colorTooltipText = $state<string | null>(null);
  let colorTooltipPosition = $state<{ x: number; y: number } | null>(null);
  let colorTooltipTimer: ReturnType<typeof setTimeout> | null = null;

  function colorLabel(color: CycleCategoryColor): string {
    return color.charAt(0).toUpperCase() + color.slice(1);
  }

  function showColorTooltip(text: string, anchor: HTMLElement) {
    if (colorTooltipTimer) clearTimeout(colorTooltipTimer);
    colorTooltipTimer = setTimeout(() => {
      const rect = anchor.getBoundingClientRect();
      colorTooltipText = text;
      // Tooltip renders above the element (translateY(-100%)), with a small gap.
      colorTooltipPosition = { x: rect.left + rect.width / 2, y: rect.top - 4 };
    }, 400);
  }

  function hideColorTooltip() {
    if (colorTooltipTimer) clearTimeout(colorTooltipTimer);
    colorTooltipTimer = null;
    colorTooltipText = null;
    colorTooltipPosition = null;
  }

  function openManageCategories() {
    manageCategoryDrafts = localCategories.map((category) => ({ ...category }));
    newManageCategoryName = '';
    newManageCategoryColor = null;
    manageCategoriesOpen = true;
  }

  function closeManageCategories() {
    closeManageColorPopover();
    if (manageCategoriesDirty) {
      manageDiscardConfirmation = true;
      return;
    }
    manageCategoriesModal.animateClose();
  }

  function discardManageCategoryChanges() {
    manageDiscardConfirmation = false;
    closeManageColorPopover();
    manageCategoriesModal.animateClose();
  }

  function syncManageDrafts() {
    manageCategoryDrafts = localCategories.map((category) => ({ ...category }));
  }

  function manageDraftUnchanged(draft: CycleCategory) {
    const original = localCategories.find((category) => category.id === draft.id);
    return !!original && original.name === draft.name.trim() && original.color === draft.color;
  }

  function categoryOrderChanged() {
    return manageCategoryDrafts.some((draft, index) => draft.id !== localCategories[index]?.id);
  }

  function moveDraftCategoryBy(index: number, direction: MoveDirection) {
    manageCategoryDrafts = moveItem(manageCategoryDrafts, index, direction) ?? manageCategoryDrafts;
  }

  function moveDraftCategory(targetId: string) {
    if (!draggedCategoryId || draggedCategoryId === targetId) return;
    const from = manageCategoryDrafts.findIndex((category) => category.id === draggedCategoryId);
    const to = manageCategoryDrafts.findIndex((category) => category.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...manageCategoryDrafts];
    next.splice(to, 0, next.splice(from, 1)[0]);
    manageCategoryDrafts = next;
  }

  let manageCategoriesDirty = $derived(
    manageCategoriesOpen &&
      (manageCategoryDrafts.some((draft) => !manageDraftUnchanged(draft)) ||
        categoryOrderChanged() ||
        newManageCategoryName.trim() !== '' ||
        newManageCategoryColor !== null),
  );
  let hasPersistableManageChanges = $derived(
    manageCategoryDrafts.some((draft) => draft.name.trim() && !manageDraftUnchanged(draft)) ||
      categoryOrderChanged() ||
      newManageCategoryName.trim() !== '',
  );

  async function saveManageCategories() {
    if (
      manageCategorySaving ||
      !hasPersistableManageChanges ||
      manageCategoryDrafts.some((draft) => !draft.name.trim())
    )
      return;
    manageCategorySaving = true;
    try {
      for (const draft of manageCategoryDrafts) {
        if (manageDraftUnchanged(draft)) continue;
        // Sequential writes so a rejected row keeps later drafts unsaved.
        // eslint-disable-next-line no-await-in-loop -- persist categories one at a time
        const response = await fetch(`/cycles/categories/${draft.id}`, {
          method: 'PATCH',
          headers: jsonHeaders(),
          body: JSON.stringify({ cycle_category: { name: draft.name.trim(), color: draft.color } }),
        });
        if (!response.ok) {
          // eslint-disable-next-line no-await-in-loop -- persist categories one at a time
          toast.error(await readApiErrorMessage(response, 'Could not update category.'));
          // Keep the in-progress draft so the user can correct a rejected change
          // (e.g. a duplicate name) instead of having their edit silently wiped.
          return;
        }
        // eslint-disable-next-line no-await-in-loop -- persist categories one at a time
        applyPayload((await response.json()) as CyclesPayload);
      }
      if (newManageCategoryName.trim()) {
        const response = await fetch('/cycles/categories', {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({
            cycle_category: {
              name: newManageCategoryName.trim(),
              color: newManageCategoryColor ?? 'slate',
            },
          }),
        });
        if (!response.ok) {
          toast.error(await readApiErrorMessage(response, 'Could not add category.'));
          return;
        }
        applyPayload((await response.json()) as CyclesPayload);
        newManageCategoryName = '';
        newManageCategoryColor = null;
      }
      const orderedIds = [
        ...manageCategoryDrafts.map((category) => category.id),
        ...localCategories
          .filter((category) => !manageCategoryDrafts.some((draft) => draft.id === category.id))
          .map((category) => category.id),
      ];
      const reorderResponse = await fetch('/cycles/categories_order', {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ cycle_categories: { ids: orderedIds } }),
      });
      if (!reorderResponse.ok) {
        toast.error(await readApiErrorMessage(reorderResponse, 'Could not reorder categories.'));
        return;
      }
      applyPayload((await reorderResponse.json()) as CyclesPayload);
      syncManageDrafts();
      closeManageColorPopover();
      manageCategoriesModal.animateClose();
    } catch {
      toast.error('Could not reach the server. Try again.');
    } finally {
      manageCategorySaving = false;
    }
  }

  function openManageColorPopover(id: string, anchor: HTMLElement) {
    manageColorStyle = 'position: fixed; left: 0; top: 0; visibility: hidden;';
    manageColorPopoverId = id;
    manageColorAnchor = anchor;
  }

  function closeManageColorPopover() {
    manageColorPopoverId = null;
    manageColorAnchor = undefined;
  }

  function pickManageColor(color: CycleCategoryColor) {
    const id = manageColorPopoverId;
    if (id === null) return;
    if (id === 'new') {
      newManageCategoryColor = color;
    } else {
      const draft = manageCategoryDrafts.find((entry) => entry.id === id);
      if (draft) draft.color = color;
    }
    closeManageColorPopover();
  }

  function requestRemoveManageCategory(draft: CycleCategory) {
    manageDeleteConfirmation = draft;
  }

  async function confirmRemoveManageCategory() {
    const draft = manageDeleteConfirmation;
    if (!draft || manageCategorySaving) return;
    manageCategorySaving = true;
    try {
      const response = await fetch(`/cycles/categories/${draft.id}`, {
        method: 'DELETE',
        headers: jsonHeaders(),
      });
      if (!response.ok) {
        toast.error(await readApiErrorMessage(response, 'Could not delete category.'));
        return;
      }
      applyPayload((await response.json()) as CyclesPayload);
      manageCategoryDrafts = manageCategoryDrafts.filter((entry) => entry.id !== draft.id);
      manageDeleteConfirmation = null;
    } catch {
      toast.error('Could not reach the server. Try again.');
    } finally {
      manageCategorySaving = false;
    }
  }

  let currentCustomCategory = $derived(
    itemForm && !categoryForName(itemForm.draft.category) ? itemForm.draft.category : null,
  );

  // The API has already applied category and item ordering. Preserve that order
  // rather than attempting a second client-side comparator for deleted/custom categories.
  let currentByCadence = $derived.by(() => {
    const groups = {} as Record<CycleCadence, CycleOccurrence[]>;
    for (const cadence of CADENCES) {
      groups[cadence] = localCurrent.filter((item) => item.cadence === cadence);
    }
    return groups;
  });
  let currentCategoryGroupsByCadence = $derived.by(() => {
    const groups = {} as Record<CycleCadence, { name: string; occurrences: CycleOccurrence[] }[]>;
    for (const cadence of CADENCES) {
      const occurrences = currentByCadence[cadence];
      const categoryNames = [
        ...localCategories.map((category) => category.name),
        ...[...new Set(occurrences.map((occurrence) => occurrence.category))]
          .filter((name) => !localCategories.some((category) => category.name === name))
          .sort((a, b) => a.localeCompare(b)),
      ];
      groups[cadence] = categoryNames
        .map((name) => ({
          name,
          occurrences: occurrences.filter((occurrence) => occurrence.category === name),
        }))
        .filter((group) => group.occurrences.length > 0);
    }
    return groups;
  });
  let activeItems = $derived(localItems.filter((item) => !item.archivedAt));
  let libraryGroups = $derived.by(() =>
    CADENCES.map((cadence) => {
      const cadenceItems = activeItems.filter((item) => item.cadence === cadence);
      const categoryNames = [
        ...localCategories.map((category) => category.name),
        ...[...new Set(cadenceItems.map((item) => item.category))]
          .filter((name) => !localCategories.some((category) => category.name === name))
          .sort((a, b) => a.localeCompare(b)),
      ];
      return {
        cadence,
        categories: categoryNames
          .map((name) => ({
            name,
            items: cadenceItems
              .filter((item) => item.category === name)
              .sort(
                (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.title.localeCompare(b.title),
              ),
          }))
          .filter((group) => group.items.length > 0),
      };
    }).filter((group) => group.categories.length > 0),
  );

  /** The items an item can be reordered among: its cadence + category group, in Library order. */
  function libraryGroup(item: CycleItem) {
    return activeItems
      .filter((other) => other.cadence === item.cadence && other.category === item.category)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.title.localeCompare(b.title));
  }

  async function reorderLibraryItems(targetId: string) {
    if (!draggedLibraryItemId || draggedLibraryItemId === targetId) return;
    const dragged = localItems.find((item) => item.id === draggedLibraryItemId);
    const target = localItems.find((item) => item.id === targetId);
    if (
      !dragged ||
      !target ||
      dragged.cadence !== target.cadence ||
      dragged.category !== target.category
    )
      return;

    await moveLibraryItemTo(
      dragged,
      libraryGroup(dragged).findIndex((item) => item.id === target.id),
    );
  }

  /** Drop and Move up / Move down: moves `moved` to index `to` of its Library group. */
  async function moveLibraryItemTo(moved: CycleItem, to: number) {
    const group = libraryGroup(moved);
    const from = group.findIndex((item) => item.id === moved.id);
    if (from < 0 || to < 0 || to >= group.length) return;
    const reordered = [...group];
    reordered.splice(to, 0, reordered.splice(from, 1)[0]);
    const originalItems = localItems;
    const byId = new Map(reordered.map((item, position) => [item.id, { ...item, position }]));
    localItems = localItems.map((item) => byId.get(item.id) ?? item);
    draggedLibraryItemId = null;
    const payload = await request('/cycles/items_order', 'PATCH', {
      cycle_items: { ids: reordered.map((item) => item.id) },
    });
    if (!payload) localItems = originalItems;
  }

  function moveDraftActionBy(index: number, direction: MoveDirection) {
    if (!itemForm) return;
    itemForm.draft.actions =
      moveItem(itemForm.draft.actions, index, direction) ?? itemForm.draft.actions;
  }

  // Touch: ⋯ action sheets stand in for the drag handles (plans/mobile-v2.md §3.5).
  let rowSheet = $state<{ title: string; actions: SheetAction[] } | null>(null);
  let rowSheetOpen = $state(false);
  function openRowSheet(title: string, actions: SheetAction[]) {
    rowSheet = { title, actions };
    rowSheetOpen = true;
  }
  function openLibraryItemSheet(item: CycleItem) {
    const group = libraryGroup(item);
    const index = group.findIndex((other) => other.id === item.id);
    const moves = moveUpDownActions(index, group.length, (direction) =>
      moveLibraryItemTo(item, index + direction),
    );
    // Phones: the card's Edit / Archive / Delete buttons live in this ⋯ too.
    openRowSheet(
      item.title,
      $viewport.isMobile
        ? [
            ...moves,
            { label: 'Edit…', icon: 'edit', onSelect: () => openEditForm(item) },
            { label: 'Archive…', icon: 'archive', onSelect: () => void toggleArchive(item) },
            {
              label: 'Delete…',
              icon: 'trash',
              danger: true,
              onSelect: () => (deleteConfirmation = item),
            },
          ]
        : moves,
    );
  }
  // Phone header ⚙: the desktop Settings menu, Manage library and Show archived.
  function openSettingsSheet() {
    openRowSheet('Cycles settings', [
      { label: 'Nudge settings', onSelect: openNudgeSettings },
      { label: 'Manage categories', onSelect: openManageCategories },
      ...(activeTab === 'library'
        ? []
        : [{ label: 'Manage library', onSelect: () => showTab('library') }]),
      ...(activeTab === 'history'
        ? [
            {
              label: showArchivedHistory ? 'Hide archived items' : 'Show archived items',
              onSelect: () => (showArchivedHistory = !showArchivedHistory),
            },
          ]
        : []),
    ]);
  }
  const TAB_HREFS = {
    current: '/cycles/current',
    library: '/cycles/library',
    history: '/cycles/history',
  } as const;
  function showTab(tab: 'current' | 'library' | 'history') {
    activeTab = tab;
    router.visit(TAB_HREFS[tab]);
  }
  function openDraftCategorySheet(index: number) {
    openRowSheet(
      manageCategoryDrafts[index].name.trim() || 'Category',
      moveUpDownActions(index, manageCategoryDrafts.length, (direction) =>
        moveDraftCategoryBy(index, direction),
      ),
    );
  }
  function openDraftActionSheet(index: number) {
    if (!itemForm) return;
    openRowSheet(
      itemForm.draft.actions[index].title.trim() || `Action ${index + 1}`,
      moveUpDownActions(index, itemForm.draft.actions.length, (direction) =>
        moveDraftActionBy(index, direction),
      ),
    );
  }

  function reorderDraftAction(targetIndex: number) {
    if (!itemForm || draggedActionIndex === null || draggedActionIndex === targetIndex) return;
    const actions = [...itemForm.draft.actions];
    actions.splice(targetIndex, 0, actions.splice(draggedActionIndex, 1)[0]);
    itemForm.draft.actions = actions;
  }

  function periodEnd(cadence: CycleCadence, periodKey: string): number | null {
    const match = periodKey.match(/^(\d{4})(?:-W(\d{2})|-([0-9]{2})|-Q([1-4]))?$/);
    if (!match) return null;
    const year = Number(match[1]);
    if (cadence === 'weekly' && match[2]) {
      // Weeks are Sunday-first (Sun-Sat); the period ends at midnight on the
      // Sunday that starts the following week.
      const sunday = getWeekStart(periodKey);
      sunday.setUTCDate(sunday.getUTCDate() + 7);
      return sunday.getTime();
    }
    if (cadence === 'monthly' && match[3]) return Date.UTC(year, Number(match[3]), 1);
    if (cadence === 'quarterly' && match[4]) return Date.UTC(year, Number(match[4]) * 3, 1);
    if (cadence === 'yearly') return Date.UTC(year + 1, 0, 1);
    return null;
  }

  let activeNudges = $derived.by(() => {
    if (!localNudgePreferences.enabled) return [];
    return CADENCES.flatMap((cadence) => {
      const incomplete = currentByCadence[cadence].filter(
        (occurrence) => !['done', 'skipped', 'not_applicable'].includes(occurrence.status),
      );
      const end = localPeriodEnds[cadence]
        ? Date.parse(localPeriodEnds[cadence]!)
        : periodEnd(cadence, localPeriods[cadence]);
      const threshold = localNudgePreferences.thresholds[cadence] * 24 * 60 * 60 * 1000;
      if (!end || incomplete.length === 0 || nudgeNow < end - threshold || nudgeNow >= end)
        return [];
      return [
        {
          cadence,
          count: incomplete.length,
          hoursLeft: Math.max(1, Math.ceil((end - nudgeNow) / 3600000)),
        },
      ];
    });
  });

  let nudgeHighlightIds = $derived.by(() => {
    if (!localNudgePreferences.enabled || !localNudgePreferences.highlight_items)
      return new Set<string>();
    return new Set(
      activeNudges.flatMap((nudge) =>
        currentByCadence[nudge.cadence]
          .filter(
            (occurrence) => !['done', 'skipped', 'not_applicable'].includes(occurrence.status),
          )
          .map((occurrence) => occurrence.id),
      ),
    );
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- interval refreshes the local countdown while the page is mounted; this is an external timer subscription.
  $effect(() => {
    const timer = window.setInterval(() => (nudgeNow = Date.now()), 60_000);
    return () => window.clearInterval(timer);
  });

  function formatTimeLeft(hours: number): string {
    if (hours >= 48) return `${Math.ceil(hours / 24)} days left`;
    if (hours > 1) return `${hours} hours left`;
    return 'less than an hour left';
  }

  function openNudgeSettings() {
    nudgeDraft = {
      enabled: localNudgePreferences.enabled,
      highlight_items: localNudgePreferences.highlight_items,
      thresholds: { ...localNudgePreferences.thresholds },
    };
    nudgeDraftBaseline = JSON.stringify(nudgeDraft);
    settingsMenuOpen = false;
    nudgeSettingsOpen = true;
  }

  function closeNudgeSettings() {
    if (nudgeDirty) {
      nudgeDiscardConfirmation = true;
      return;
    }
    nudgeModal.animateClose();
  }

  function discardNudgeChanges() {
    nudgeDiscardConfirmation = false;
    nudgeModal.animateClose();
  }

  async function saveNudgePreferences() {
    nudgeSaving = true;
    try {
      const response = await fetch('/preferences/cycle_nudges', {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({
          enabled: nudgeDraft.enabled,
          highlight_items: nudgeDraft.highlight_items,
          thresholds: nudgeDraft.thresholds,
        }),
      });
      if (!response.ok) {
        toast.error(await readApiErrorMessage(response, 'Could not save nudge settings.'));
        return;
      }
      const payload = (await response.json()) as { cycleNudgePreferences: CycleNudgePreferences };
      localNudgePreferences = payload.cycleNudgePreferences;
      nudgeModal.animateClose();
      toast.success('Cycle nudge settings saved.');
    } catch {
      toast.error('Could not reach the server. Try again.');
    } finally {
      nudgeSaving = false;
    }
  }
  let archivedItems = $derived(localItems.filter((item) => item.archivedAt));
  let deletePastRecordCount = $derived.by(() => {
    const target = deleteConfirmation;
    if (!target) return 0;
    // Count occurrences that are not the item's current-period row. Compare against
    // the period key for the item's current cadence (not the occurrence's own), so
    // snapshots left behind by a cadence change still count as past records.
    const currentPeriod = localPeriods[target.cadence];
    return localHistory.filter(
      (occurrence) => occurrence.itemId === target.id && occurrence.periodKey !== currentPeriod,
    ).length;
  });
  // === History grid deriveds ===
  let currentWeekKey = $derived(localPeriods.weekly);
  let historyOccurrencesByItem = $derived.by(() => {
    const map = new Map<string, CycleOccurrence[]>();
    for (const occurrence of localHistory) {
      const list = map.get(occurrence.itemId) ?? [];
      list.push(occurrence);
      map.set(occurrence.itemId, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.periodKey.localeCompare(b.periodKey));
    }
    return map;
  });
  let clampedHistoryEndWeek = $derived.by(() => {
    const current = currentWeekKey;
    const end = historyEndWeek ?? current;
    if (!current || !end) return undefined;
    return compareWeekKeys(end, current) > 0 ? current : end;
  });
  const MOBILE_HISTORY_WEEKS = 4;
  const TABLET_HISTORY_WEEKS = 8;
  const DESKTOP_HISTORY_WEEKS = 12;
  const historyWeekCount = () =>
    $viewport.isMobile
      ? MOBILE_HISTORY_WEEKS
      : $viewport.isTablet
        ? TABLET_HISTORY_WEEKS
        : DESKTOP_HISTORY_WEEKS;
  let visibleHistoryWeeks = $derived.by(() => {
    const end = clampedHistoryEndWeek;
    if (!end) return [];
    const count = historyWeekCount();
    return Array.from({ length: count }, (_, index) => shiftWeekKey(end, index - (count - 1)));
  });
  let historyRangeLabel = $derived.by(() => {
    const weeks = visibleHistoryWeeks;
    if (weeks.length === 0) return '';
    const start = getWeekStart(weeks[0]);
    const end = getWeekEnd(weeks[weeks.length - 1]);
    const format = (date: Date, options: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' }).format(date);
    const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
    return sameYear
      ? `${format(start, { month: 'short', day: 'numeric' })} - ${format(end, { month: 'short', day: 'numeric' })}`
      : `${format(start, { month: 'short', day: 'numeric', year: 'numeric' })} - ${format(end, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  });
  let historyRangeDates = $derived.by(() => {
    const weeks = visibleHistoryWeeks;
    const iso = (date: Date) => date.toISOString().slice(0, 10);
    // No weekly period known: keep the toolbar (as before) with a one-day range.
    if (weeks.length === 0) return { start: iso(new Date()), end: iso(new Date()) };
    return { start: iso(getWeekStart(weeks[0])), end: iso(getWeekEnd(weeks[weeks.length - 1])) };
  });
  let historyWeeksBehind = $derived.by(() => {
    const current = currentWeekKey;
    const end = clampedHistoryEndWeek;
    if (!current || !end) return 0;
    return Math.round((getWeekStart(current).getTime() - getWeekStart(end).getTime()) / 604800000);
  });
  type HistoryEntry = { item: CycleItem; latestOccurrence?: CycleOccurrence };
  type HistoryCadenceGroup = { cadence: CycleCadence; entries: HistoryEntry[] };
  type HistoryGroup = { name: string; color: CycleCategoryColor; cadences: HistoryCadenceGroup[] };
  let historyGroups = $derived.by(() => {
    const visibleItems = localItems.filter((item) => showArchivedHistory || !item.archivedAt);
    const namesInUse = new Set(visibleItems.map((item) => item.category));
    const orderedNames = [
      ...localCategories.map((category) => category.name).filter((name) => namesInUse.has(name)),
      ...[...namesInUse]
        .filter((name) => !localCategories.some((category) => category.name === name))
        .sort((a, b) => a.localeCompare(b)),
    ];
    return orderedNames
      .map((name) => {
        const categoryItems = visibleItems
          .filter((item) => item.category === name)
          .sort(
            (a, b) =>
              CADENCES.indexOf(a.cadence) - CADENCES.indexOf(b.cadence) ||
              (a.position ?? 0) - (b.position ?? 0) ||
              a.title.localeCompare(b.title),
          );
        const cadences = CADENCES.map((cadence) => ({
          cadence,
          entries: categoryItems
            .filter((item) => item.cadence === cadence)
            .map((item) => ({
              item,
              latestOccurrence: historyOccurrencesByItem.get(item.id)?.at(-1),
            })),
        })).filter((cadenceGroup) => cadenceGroup.entries.length > 0);
        return { name, color: categoryColorFor(name), cadences };
      })
      .filter((group) => group.cadences.length > 0);
  });

  /** Inclusive UTC start/end dates for a cadence period key, or null if malformed. */
  function periodBounds(
    cadence: CycleCadence,
    periodKey: string,
  ): { start: Date; end: Date } | null {
    if (cadence === 'weekly') {
      if (!/^(\d{4})-W(\d{2})$/.test(periodKey)) return null;
      // Sunday-first week (Sun-Sat), matching the app-wide Project/calendar
      // convention. getWeekStart/getWeekEnd return UTC dates, so the range is
      // timezone-independent and stable for every user.
      return { start: getWeekStart(periodKey), end: getWeekEnd(periodKey) };
    }
    if (cadence === 'monthly') {
      const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
      if (!match) return null;
      return {
        start: new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)),
        end: new Date(Date.UTC(Number(match[1]), Number(match[2]), 0)),
      };
    }
    if (cadence === 'quarterly') {
      const match = /^(\d{4})-Q([1-4])$/.exec(periodKey);
      if (!match) return null;
      const month = (Number(match[2]) - 1) * 3;
      return {
        start: new Date(Date.UTC(Number(match[1]), month, 1)),
        end: new Date(Date.UTC(Number(match[1]), month + 3, 0)),
      };
    }
    if (!/^\d{4}$/.test(periodKey)) return null;
    return {
      start: new Date(Date.UTC(Number(periodKey), 0, 1)),
      end: new Date(Date.UTC(Number(periodKey), 11, 31)),
    };
  }

  function periodRange(cadence: CycleCadence, periodKey: string): string {
    const bounds = periodBounds(cadence, periodKey);
    if (!bounds) return periodKey;
    const { start, end } = bounds;
    const format = (date: Date, options: Intl.DateTimeFormatOptions) =>
      // Dates are constructed in UTC, so render them as UTC to avoid the
      // browser's local timezone shifting the displayed range by a day.
      new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' }).format(date);
    if (cadence === 'yearly') return '';
    if (cadence === 'monthly') return format(start, { month: 'short', year: 'numeric' });
    if (cadence === 'quarterly') {
      return `${format(start, { month: 'short' })} - ${format(end, { month: 'short', year: 'numeric' })}`;
    }
    const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
    return sameYear
      ? `${format(start, { month: 'short', day: 'numeric' })} - ${format(end, { month: 'short', day: 'numeric', year: 'numeric' })}`
      : `${format(start, { month: 'short', day: 'numeric', year: 'numeric' })} - ${format(end, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }

  // === History grid week helpers ===
  // The weekly period key labels a Sunday-Saturday week by the ISO week of its
  // Monday (the same convention as Cycles::Occurrences#period_key_for).
  function isoWeek1Monday(year: number): Date {
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const dayOfWeek = jan4.getUTCDay() || 7; // Mon=1..Sun=7
    const monday = new Date(jan4);
    monday.setUTCDate(jan4.getUTCDate() - (dayOfWeek - 1));
    return monday;
  }

  /** The Sunday that starts the week containing the given date. */
  function sundayContaining(date: Date): Date {
    const sunday = new Date(date);
    sunday.setUTCDate(date.getUTCDate() - date.getUTCDay());
    return sunday;
  }

  /** Inverse of getWeekStart: maps a week-starting Sunday back to its week key. */
  function weekKeyForSunday(sunday: Date): string {
    const monday = new Date(sunday);
    monday.setUTCDate(sunday.getUTCDate() + 1);
    let year = monday.getUTCFullYear();
    let week1 = isoWeek1Monday(year);
    if (monday < week1) {
      year -= 1;
      week1 = isoWeek1Monday(year);
    } else {
      const nextWeek1 = isoWeek1Monday(year + 1);
      if (monday >= nextWeek1) {
        year += 1;
        week1 = nextWeek1;
      }
    }
    const week = Math.round((monday.getTime() - week1.getTime()) / 604800000) + 1;
    return `${year}-W${String(week).padStart(2, '0')}`;
  }

  function shiftWeekKey(weekKey: string, weeks: number): string {
    const sunday = getWeekStart(weekKey);
    sunday.setUTCDate(sunday.getUTCDate() + weeks * 7);
    return weekKeyForSunday(sunday);
  }

  function compareWeekKeys(a: string, b: string): number {
    return getWeekStart(a).getTime() - getWeekStart(b).getTime();
  }

  function historyWeekLabel(weekKey: string): string {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(getWeekStart(weekKey));
  }

  function navigateHistory(weeks: number) {
    const end = clampedHistoryEndWeek ?? currentWeekKey;
    if (!end) return;
    if (weeks > 0 && currentWeekKey) {
      const target = shiftWeekKey(end, weeks);
      historyEndWeek = compareWeekKeys(target, currentWeekKey) > 0 ? currentWeekKey : target;
    } else {
      historyEndWeek = shiftWeekKey(end, weeks);
    }
  }

  function goHistoryToday() {
    if (currentWeekKey) historyEndWeek = currentWeekKey;
  }

  /**
   * Resolves the grid cell for an item in a week column. Weekly items map
   * one-to-one onto week keys. Non-weekly periods are rendered as a single
   * status-bearing cell in the week containing the period's deadline (clamped
   * to the current week for the open period); other weeks the period spans are
   * shaded. Weeks without a snapshot (item not yet created or archived) are
   * untracked.
   */
  type HistoryCell =
    | { kind: 'status'; occurrence: CycleOccurrence }
    | { kind: 'unrelatedPeriod' }
    | { kind: 'untracked' };
  function historyCellFor(item: CycleItem, weekKey: string): HistoryCell {
    const occurrences = (historyOccurrencesByItem.get(item.id) ?? []).filter(
      (occurrence) => occurrence.cadence === item.cadence,
    );
    if (item.cadence === 'weekly') {
      const occurrence = occurrences.find((entry) => entry.periodKey === weekKey);
      return occurrence ? { kind: 'status', occurrence } : { kind: 'untracked' };
    }
    const weekStart = getWeekStart(weekKey);
    const weekEnd = getWeekEnd(weekKey);
    for (const occurrence of occurrences) {
      const bounds = periodBounds(item.cadence, occurrence.periodKey);
      if (!bounds) continue;
      if (weekStart > bounds.end || weekEnd < bounds.start) continue;
      let deadlineSunday = sundayContaining(bounds.end);
      if (currentWeekKey) {
        const currentSunday = getWeekStart(currentWeekKey);
        if (deadlineSunday > currentSunday) deadlineSunday = currentSunday;
      }
      if (weekKey === weekKeyForSunday(deadlineSunday)) {
        return { kind: 'status', occurrence };
      }
      return { kind: 'unrelatedPeriod' };
    }
    return { kind: 'untracked' };
  }

  const HISTORY_COMPLETED_STATUSES = new Set<CycleStatus>(['done', 'skipped', 'not_applicable']);
  function isHistoryCompleted(status: CycleStatus) {
    return HISTORY_COMPLETED_STATUSES.has(status);
  }

  function historyGroupProgress(entries: HistoryEntry[], weekKey: string) {
    let total = 0;
    let resolved = 0;
    for (const entry of entries) {
      const cell = historyCellFor(entry.item, weekKey);
      if (cell.kind !== 'status') continue;
      total += 1;
      if (isHistoryCompleted(cell.occurrence.status)) resolved += 1;
    }
    return { total, resolved };
  }

  // === History cell tooltip (app-style dark pill, like the habits color tooltip) ===
  const HISTORY_TOOLTIP_DELAY_MS = 400;
  let historyTooltipTimer: ReturnType<typeof setTimeout> | undefined;
  let historyTooltip: { text: string; x: number; y: number } | null = $state(null);
  function showHistoryTooltip(text: string, anchor: HTMLElement) {
    clearTimeout(historyTooltipTimer);
    historyTooltipTimer = setTimeout(() => {
      const rect = anchor.getBoundingClientRect();
      historyTooltip = { text, x: rect.left + rect.width / 2, y: rect.top - 4 };
    }, HISTORY_TOOLTIP_DELAY_MS);
  }
  function hideHistoryTooltip() {
    clearTimeout(historyTooltipTimer);
    historyTooltipTimer = undefined;
    historyTooltip = null;
  }
  /** Hover/focus message for a cell: what it shows, or why it is not applicable. */
  function historyCellTooltip(item: CycleItem, cell: HistoryCell): string {
    if (cell.kind === 'status') {
      return `${STATUS_LABELS[cell.occurrence.status]} · Click to change status`;
    }
    if (cell.kind === 'unrelatedPeriod') {
      return `Not applicable this week · ${CADENCE_LABELS[item.cadence]} items are tracked in their deadline week`;
    }
    return 'Not applicable this week';
  }

  // Long-press (touch/pen) opens the status menu, mirroring the habits board's
  // press-and-hold interaction.
  const HISTORY_LONG_PRESS_MS = 600;
  const HISTORY_LONG_PRESS_MOVE_PX = 10;
  let historyPressTimer: ReturnType<typeof setTimeout> | undefined;
  let historyPress:
    | { occurrence: CycleOccurrence; anchor: HTMLElement; pointerId: number; x: number; y: number }
    | undefined;
  // Browsers dispatch a click after the finger lifts from a long press. Ignore
  // that one click, keyed to the pressed cell, so opening the status menu does
  // not also toggle the cell (mirrors the Habits board's suppression).
  let suppressHistoryCellClick: { occurrenceId: string; expiresAt: number } | undefined;
  function clearHistoryPress() {
    if (historyPressTimer !== undefined) clearTimeout(historyPressTimer);
    historyPressTimer = undefined;
    historyPress = undefined;
  }
  function historyCellPointerDown(event: PointerEvent, occurrence: CycleOccurrence) {
    if (event.pointerType === 'mouse' || event.isPrimary === false) return;
    clearHistoryPress();
    historyPress = {
      occurrence,
      anchor: event.currentTarget as HTMLElement,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
    historyPressTimer = setTimeout(() => {
      const press = historyPress;
      historyPressTimer = undefined;
      if (!press || saving) return;
      suppressHistoryCellClick = {
        occurrenceId: press.occurrence.id,
        expiresAt: Date.now() + 1000,
      };
      openStatusMenu(press.occurrence, press.anchor);
    }, HISTORY_LONG_PRESS_MS);
  }
  function historyCellPointerMove(event: PointerEvent) {
    const press = historyPress;
    if (!press || press.pointerId !== event.pointerId) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > HISTORY_LONG_PRESS_MOVE_PX) {
      clearHistoryPress();
    }
  }
  function historyCellPointerEnd(event: PointerEvent) {
    if (historyPress?.pointerId === event.pointerId) clearHistoryPress();
  }
  /** Opens the status menu from a status cell (left click, right-click, or long-press). */
  function openHistoryStatusMenu(event: MouseEvent, occurrence: CycleOccurrence) {
    event.preventDefault();
    // Some touch browsers emit contextmenu or a synthesized click after a long
    // press that already opened the menu for this cell; do not reopen it in
    // that case. A matching suppression also means that follow-up click is
    // already swallowed.
    if (
      suppressHistoryCellClick?.occurrenceId === occurrence.id &&
      Date.now() <= suppressHistoryCellClick.expiresAt
    ) {
      return;
    }
    suppressHistoryCellClick = undefined;
    hideHistoryTooltip();
    if (saving) return;
    openStatusMenu(occurrence, event.currentTarget as HTMLElement);
  }
  onDestroy(() => {
    clearHistoryPress();
    clearTimeout(historyTooltipTimer);
  });

  function applyPayload(payload: CyclesPayload) {
    // The server always serializes actions; defaulting keeps older cached/test payloads
    // compatible while responses roll out.
    localCurrent = payload.currentOccurrences.map((occurrence) => ({
      ...occurrence,
      actions: occurrence.actions ?? [],
    }));
    localItems = payload.items.map((item) => ({ ...item, actions: item.actions ?? [] }));
    localHistory = payload.historyOccurrences.map((occurrence) => ({
      ...occurrence,
      actions: occurrence.actions ?? [],
    }));
    localPeriods = payload.currentPeriods;
    if (payload.currentPeriodEnds) localPeriodEnds = payload.currentPeriodEnds;
    localCategories = payload.categories;
    if (payload.cycleNudgePreferences) localNudgePreferences = payload.cycleNudgePreferences;
  }

  async function request(
    path: string,
    method: string,
    body?: object,
  ): Promise<CyclesPayload | null> {
    saving = true;
    try {
      const response = await fetch(path, {
        method,
        headers: jsonHeaders(),
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!response.ok) {
        toast.error(await readApiErrorMessage(response, 'Could not save your cycle.'));
        return null;
      }
      const payload = (await response.json()) as CyclesPayload;
      applyPayload(payload);
      return payload;
    } catch {
      toast.error('Could not reach the server. Try again.');
      return null;
    } finally {
      saving = false;
    }
  }

  function openCreateForm(cadence: CycleCadence = 'weekly') {
    itemForm = { mode: 'create', draft: blankDraft(cadence) };
    itemFormBaseline = JSON.stringify(itemForm.draft);
  }

  function openEditForm(item: CycleItem) {
    itemForm = {
      mode: 'edit',
      item,
      draft: {
        title: item.title,
        category: item.category,
        cadence: item.cadence,
        instructions: item.instructions || '',
        resource_url: item.resourceUrl || '',
        estimated_duration: item.estimatedDuration || '',
        actions: item.actions.map((action) => ({ id: action.id, title: action.title })),
      },
    };
    itemFormBaseline = JSON.stringify(itemForm.draft);
  }

  async function submitItemForm(e: SubmitEvent) {
    e.preventDefault();
    if (!itemForm) return;
    if (!itemForm.draft.title.trim()) return;

    if (itemForm.mode === 'create') {
      const payload = await request('/cycles/items', 'POST', { cycle_item: itemForm.draft });
      if (payload) {
        const created = payload.createdItemId
          ? payload.items.find((item) => item.id === payload.createdItemId)
          : undefined;
        toast.success('Added to your current cycle.', {
          action: created ? { label: 'Undo', onClick: () => void deleteItem(created) } : undefined,
        });
        finishClosingItemModal();
      }
    } else {
      if (
        await request(`/cycles/items/${itemForm.item.id}`, 'PATCH', {
          cycle_item: itemForm.draft,
        })
      ) {
        toast.success('Checklist item updated.');
        finishClosingItemModal();
      }
    }
  }

  function finishClosingItemModal() {
    closeCategoryMenu();
    itemModal.animateClose();
  }

  function closeItemModal() {
    closeCategoryMenu();
    if (itemFormDirty) {
      itemDiscardConfirmation = true;
      return;
    }
    finishClosingItemModal();
  }

  function discardItemChanges() {
    itemDiscardConfirmation = false;
    finishClosingItemModal();
  }

  function handleItemModalKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      closeItemModal();
    }
  }

  function handleNudgeModalKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && nudgeSettingsOpen) closeNudgeSettings();
  }

  async function deleteItem(item: CycleItem): Promise<boolean> {
    return (await request(`/cycles/items/${item.id}`, 'DELETE')) !== null;
  }

  async function restoreItem(item: CycleItem): Promise<boolean> {
    if (await request(`/cycles/items/${item.id}/restore`, 'POST')) {
      toast.success('Item restored.');
      return true;
    }
    return false;
  }

  async function toggleArchive(item: CycleItem) {
    if (!item.archivedAt) {
      archiveConfirmation = item;
      return;
    }
    await restoreItem(item);
  }

  async function confirmArchive() {
    const item = archiveConfirmation;
    if (!item) return;
    if (await request(`/cycles/items/${item.id}/archive`, 'POST')) {
      archiveConfirmation = null;
      toast.success('Item archived from your checklist and future cycles.', {
        action: { label: 'Undo', onClick: () => void restoreItem(item) },
      });
    }
  }

  async function confirmDelete() {
    const item = deleteConfirmation;
    if (!item) return;
    if (await deleteItem(item)) {
      deleteConfirmation = null;
      toast.success('Checklist item deleted.');
    }
  }

  async function updateOccurrenceAction(action: CycleOccurrenceAction, completed: boolean) {
    await request(`/cycles/actions/${action.id}`, 'PATCH', {
      cycle_occurrence_action: { completed },
    });
  }

  async function updateOccurrence(
    occurrence: CycleOccurrence,
    changes: Partial<Pick<CycleOccurrence, 'status' | 'notes'>>,
  ) {
    await request(`/cycles/occurrences/${occurrence.id}`, 'PATCH', { cycle_occurrence: changes });
  }

  async function closeNotesPopover(): Promise<boolean> {
    const popover = notesPopover;
    if (!popover) return true;
    if (popover.draft !== (popover.occurrence.notes || '')) {
      const saved = await request(`/cycles/occurrences/${popover.occurrence.id}`, 'PATCH', {
        cycle_occurrence: { notes: popover.draft },
      });
      if (!saved) return false;
    }
    notesPopover = null;
    return true;
  }

  async function openNotesPopover(occurrence: CycleOccurrence) {
    if (notesPopover?.occurrence.id === occurrence.id) return;
    if (!(await closeNotesPopover())) return;
    notesPopover = { occurrence, draft: occurrence.notes || '' };
  }

  // History is now a weekly grid over all cadences; no per-cadence selector.

  function resolvedCount(occurrenceList: CycleOccurrence[]) {
    return occurrenceList.filter((item) =>
      ['done', 'skipped', 'not_applicable'].includes(item.status),
    ).length;
  }

  function isMobile() {
    return $viewport.isMobile;
  }

  // Tablets need the same prioritized occurrence layout as phones, while retaining
  // their desktop page chrome and controls.
  function isCompactChecklist() {
    return $viewport.isMobile || $viewport.isTablet;
  }

  // === Status dropdowns ===
  type StatusDropdown = { open: boolean; occurrence: CycleOccurrence | null };
  let statusMenu = $state<StatusDropdown>({ open: false, occurrence: null });
  let statusMenuAnchor: HTMLElement | undefined = $state();
  let statusMenuEl = $state<HTMLDivElement | undefined>();
  let statusMenuStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');

  async function positionStatusMenu(
    anchor: HTMLElement,
    menu: HTMLElement,
    setStyle: (style: string) => void,
  ) {
    const result = await computePosition(anchor, menu, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [
        offset(4),
        flip({ fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }),
        shift({ padding: 8 }),
      ],
    });
    setStyle(`position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`);
  }

  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate lifecycle: subscribes while an anchored menu is open.
  $effect(() => {
    if (!statusMenu.open || !statusMenuAnchor || !statusMenuEl) return;
    return autoUpdate(statusMenuAnchor, statusMenuEl, () =>
      positionStatusMenu(statusMenuAnchor!, statusMenuEl!, (style) => (statusMenuStyle = style)),
    );
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate lifecycle: subscribes while the category picker is open.
  $effect(() => {
    if (!categoryMenuOpen || !categoryMenuAnchor || !categoryMenuEl) return;
    return autoUpdate(categoryMenuAnchor, categoryMenuEl, () =>
      positionStatusMenu(
        categoryMenuAnchor!,
        categoryMenuEl!,
        (style) => (categoryMenuStyle = style),
      ),
    );
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate lifecycle: subscribes while the manage color picker is open.
  $effect(() => {
    if (manageColorPopoverId === null || !manageColorAnchor || !manageColorEl) return;
    return autoUpdate(manageColorAnchor, manageColorEl, () =>
      positionStatusMenu(manageColorAnchor!, manageColorEl!, (style) => (manageColorStyle = style)),
    );
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- document-level dismissal while the color popover is open; the modal card stops click propagation so only a document listener catches outside clicks. mousedown precedes the opening click, so no deferral is needed.
  $effect(() => {
    if (manageColorPopoverId === null) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (manageColorAnchor?.contains(target) || manageColorEl?.contains(target)) return;
      closeManageColorPopover();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  });

  function openStatusMenu(occurrence: CycleOccurrence, anchor: HTMLElement) {
    statusMenuStyle = 'position: fixed; left: 0; top: 0; visibility: hidden;';
    statusMenu = { open: true, occurrence };
    statusMenuAnchor = anchor;
  }

  function closeStatusMenu() {
    statusMenu = { open: false, occurrence: null };
    statusMenuAnchor = undefined;
  }

  async function pickStatus(status: CycleStatus) {
    if (!statusMenu.occurrence) return;
    const occurrence = statusMenu.occurrence;
    closeStatusMenu();
    await updateOccurrence(occurrence, { status });
  }

  function handleStatusKeydown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;
    if (categoryMenuOpen) {
      e.preventDefault();
      closeCategoryMenu();
      return;
    }
    if (manageColorPopoverId !== null) {
      e.preventDefault();
      closeManageColorPopover();
      return;
    }
    if (statusMenu.open) {
      e.preventDefault();
      closeStatusMenu();
    }
    if (notesPopover) {
      e.preventDefault();
      closeNotesPopover();
    }
    if (archiveConfirmation) {
      e.preventDefault();
      archiveModal.animateClose();
    }
    if (deleteConfirmation) {
      e.preventDefault();
      deleteModal.animateClose();
    }
    if (manageDeleteConfirmation) {
      e.preventDefault();
      manageDeleteModal.animateClose();
      return;
    }
    if (manageDiscardConfirmation) {
      e.preventDefault();
      manageDiscardModal.animateClose();
      return;
    }
    if (manageCategoriesOpen) {
      e.preventDefault();
      closeManageCategories();
    }
  }

  function handleStatusOutsideClick(e: MouseEvent) {
    const target = e.target as Node;
    if (settingsMenuOpen && !(target as Element).closest('.settings-menu-wrap')) {
      settingsMenuOpen = false;
    }
    if (categoryMenuOpen) {
      if (!categoryMenuAnchor?.contains(target) && !categoryMenuEl?.contains(target)) {
        closeCategoryMenu();
      }
    }
    if (statusMenu.open) {
      if (!statusMenuAnchor?.contains(target)) {
        const menuEl = document.querySelector('.status-menu-popover');
        if (!menuEl?.contains(target)) closeStatusMenu();
      }
    }
    if (notesPopover) {
      const popoverEl = document.querySelector('.notes-popover');
      if (!popoverEl?.contains(target)) closeNotesPopover();
    }
  }
</script>

<svelte:head><title>Cycles · Momentium</title></svelte:head>

<svelte:window
  onkeydown={(e) => {
    handleStatusKeydown(e);
    handleItemModalKeydown(e);
    handleNudgeModalKeydown(e);
  }}
/>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="cycles-page" onclick={handleStatusOutsideClick}>
  {#if isMobile()}
    <MobileTopBar title="Cycles">
      {#snippet actions()}
        <button
          type="button"
          class="mtb-icon-btn"
          aria-label="Add checklist item"
          onclick={() => openCreateForm()}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
            ><path d="M12 5v14M5 12h14" /></svg
          >
        </button>
        <button
          type="button"
          class="mtb-icon-btn"
          aria-label="Open Cycles settings"
          aria-haspopup="dialog"
          onclick={openSettingsSheet}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 1 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"
            />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
      {/snippet}
      {#snippet below()}
        <SegmentedControl
          label="Cycles views"
          value={activeTab}
          options={[
            { value: 'current', label: 'Current', href: TAB_HREFS.current },
            {
              value: 'library',
              label: 'Library',
              count: activeItems.length,
              href: TAB_HREFS.library,
            },
            { value: 'history', label: 'History', href: TAB_HREFS.history },
          ]}
          onChange={(tab) => (activeTab = tab)}
        />
      {/snippet}
    </MobileTopBar>
  {:else}
    <header class="page-header">
      <div class="header-left">
        <Link href="/" class="logo">
          <img src={momentiumLogo} alt="Momentium" class="logo-icon" />
          <span class="logo-text">Momentium</span>
        </Link>
        <NavDropdown activePage="cycles" />
      </div>
      <ProfileDropdown />
    </header>
  {/if}

  <main class="page-body">
    <div class="content-container">
      {#if !isMobile()}
        <section class="page-intro">
          <div>
            <h1>Cycles</h1>
            <p class="hero-copy">
              Recurring checklists for your week, month, quarter, and year. This is for periodic
              life maintenance and reflection—not daily habits or project tasks.
            </p>
          </div>
          <div class="page-intro-actions">
            <div class="settings-menu-wrap">
              <button
                type="button"
                class="quiet settings-trigger"
                aria-label="Open Cycles settings"
                aria-haspopup="menu"
                aria-expanded={settingsMenuOpen}
                onclick={(event) => {
                  event.stopPropagation();
                  settingsMenuOpen = !settingsMenuOpen;
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 1 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linejoin="round"
                  />
                  <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" />
                </svg>
                <span>Settings</span>
                <svg
                  class="settings-chevron"
                  viewBox="0 0 10 6"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.2" fill="none" />
                </svg>
              </button>
              <div
                class="settings-menu"
                class:open={settingsMenuOpen}
                role="menu"
                tabindex="-1"
                onclick={(event) => event.stopPropagation()}
              >
                <button type="button" role="menuitem" onclick={openNudgeSettings}
                  >Nudge settings</button
                >
                <button
                  type="button"
                  role="menuitem"
                  onclick={() => {
                    settingsMenuOpen = false;
                    openManageCategories();
                  }}>Manage categories</button
                >
              </div>
            </div>
            <button class="primary" type="button" onclick={() => openCreateForm()}
              >+ Add checklist item</button
            >
          </div>
        </section>

        <nav class="tabs" aria-label="Cycles views">
          <Link
            class={`tab ${activeTab === 'current' ? 'active' : ''}`}
            href="/cycles/current"
            onclick={() => (activeTab = 'current')}>Current cycle</Link
          >
          <Link
            class={`tab ${activeTab === 'library' ? 'active' : ''}`}
            href="/cycles/library"
            onclick={() => (activeTab = 'library')}
            >Library <span class="tab-count">{activeItems.length}</span></Link
          >
          <Link
            class={`tab ${activeTab === 'history' ? 'active' : ''}`}
            href="/cycles/history"
            onclick={() => (activeTab = 'history')}>History</Link
          >
        </nav>
      {/if}

      {#if activeTab === 'current'}
        {#if activeNudges.length > 0}
          <section class="cycle-nudge" class:slim={isMobile()} aria-label="End-of-period reminders">
            <div class="nudge-spark" aria-hidden="true">✦</div>
            {#if isMobile()}
              <p class="nudge-slim-copy">
                {#each activeNudges as nudge, index}{index > 0 ? ' · ' : ''}<b>{nudge.count}</b>
                  {CADENCE_LABELS[nudge.cadence].toLowerCase()}{/each}{` · ${formatTimeLeft(
                  Math.min(...activeNudges.map((nudge) => nudge.hoursLeft)),
                )}`}
              </p>
            {:else}
              <div class="nudge-copy">
                <strong>A tiny finish-line nudge</strong>
                <span>
                  {#each activeNudges as nudge, index}
                    {#if index > 0}<span aria-hidden="true"> · </span>{/if}
                    <b>{nudge.count} {nudge.count === 1 ? 'item' : 'items'}</b> in {CADENCE_LABELS[
                      nudge.cadence
                    ].toLowerCase()}
                    {formatTimeLeft(nudge.hoursLeft)}
                  {/each}
                </span>
              </div>
            {/if}
            <button
              type="button"
              class="nudge-close"
              aria-label="Open nudge settings"
              onclick={openNudgeSettings}>Settings</button
            >
          </section>
        {/if}
        <section class="current-view">
          {#if !isMobile()}
            <div class="workspace-heading">
              <div>
                <h2>Current checklist</h2>
                <p>Resolve what applies to this period, or leave it incomplete for the record.</p>
              </div>
              <span class="manage-library-link">
                <Link href="/cycles/library" onclick={() => (activeTab = 'library')}
                  >Manage library</Link
                >
              </span>
            </div>
          {/if}

          <div class="cadence-tables">
            {#each CADENCES as cadence}
              {@const occurrences = currentByCadence[cadence]}
              {@const categoryGroups = currentCategoryGroupsByCadence[cadence]}
              {@const resolved = resolvedCount(occurrences)}
              <section class="cadence-table" aria-labelledby={`${cadence}-heading`}>
                <header class="cadence-header">
                  <div class="cadence-heading">
                    <h3 id={`${cadence}-heading`}>
                      {CADENCE_LABELS[cadence]}
                      {#if periodRange(cadence, localPeriods[cadence])}
                        <span class="cadence-heading-range">
                          ({periodRange(cadence, localPeriods[cadence])})</span
                        >
                      {/if}
                    </h3>
                    <span class="period-pill">{localPeriods[cadence]}</span>
                  </div>
                  <div class="cadence-header-actions">
                    {#if occurrences.length > 0}
                      <div class="cadence-progress">
                        <div
                          class="progress-track"
                          role="progressbar"
                          aria-label={`${CADENCE_LABELS[cadence]} progress: ${resolved} of ${occurrences.length} resolved`}
                          aria-valuemin="0"
                          aria-valuemax={occurrences.length}
                          aria-valuenow={resolved}
                        >
                          <span
                            class="progress-fill"
                            style={`width: ${(resolved / occurrences.length) * 100}%`}
                          ></span>
                        </div>
                        <span class="progress">{resolved}/{occurrences.length} resolved</span>
                      </div>
                    {/if}
                    <button
                      type="button"
                      class="section-add"
                      aria-label={`Add ${CADENCE_LABELS[cadence]} item`}
                      onclick={() => openCreateForm(cadence)}
                    >
                      <span class="add-icon" aria-hidden="true">+</span>
                      <span>Add</span>
                    </button>
                  </div>
                </header>
                <div class="table-wrap">
                  <div class="sheet-row sheet-column-header" aria-hidden="true">
                    <span>Task</span>
                    <span>Actions</span>
                    <span>Resource</span>
                    <span>Estimate</span>
                    <span>Status</span>
                    <span>Notes</span>
                  </div>
                  {#if occurrences.length === 0}
                    <div class="sheet-empty">
                      <span>No active {cadence} items yet.</span>
                      <button
                        type="button"
                        class="section-add empty"
                        onclick={() => openCreateForm(cadence)}
                      >
                        <span class="add-icon" aria-hidden="true">+</span>
                        Add {CADENCE_LABELS[cadence]} item
                      </button>
                    </div>
                  {:else}
                    <div class="sheet-rows">
                      {#each categoryGroups as categoryGroup (categoryGroup.name)}
                        {@const categoryResolved = resolvedCount(categoryGroup.occurrences)}
                        <section
                          class="sheet-category-group"
                          aria-labelledby={`current-${cadence}-${categoryGroup.name}-heading`}
                        >
                          <div class="category-divider" style={tagStyle(categoryGroup.name)}>
                            <h4 id={`current-${cadence}-${categoryGroup.name}-heading`}>
                              <span class="category-color-dot" aria-hidden="true"></span>
                              <span class="category-group-name">{categoryGroup.name}</span>
                              <span class="category-group-progress"
                                >{categoryResolved}/{categoryGroup.occurrences.length} resolved</span
                              >
                            </h4>
                          </div>
                          <div class="sheet-category-items">
                            {#each categoryGroup.occurrences as occurrence (occurrence.id)}
                              <article
                                class="sheet-row occurrence"
                                class:nudge-highlight={nudgeHighlightIds.has(occurrence.id)}
                                data-tone={occurrence.status}
                              >
                                <div class="sheet-cell checklist-cell" data-label="Task">
                                  <h4>
                                    <button
                                      type="button"
                                      class="checklist-title"
                                      disabled={saving}
                                      onclick={() => {
                                        const item = localItems.find(
                                          (candidate) => candidate.id === occurrence.itemId,
                                        );
                                        if (item) openEditForm(item);
                                      }}
                                    >
                                      {#if nudgeHighlightIds.has(occurrence.id)}
                                        <span
                                          class="nudge-item-spark"
                                          title="Near the end of this period"
                                          aria-label="Near the end of this period">✦</span
                                        >
                                      {/if}
                                      {occurrence.title}
                                    </button>
                                  </h4>
                                  {#if occurrence.instructions}
                                    <p
                                      class="checklist-instructions"
                                      class:expanded={expandedInstructions[occurrence.id]}
                                      use:instructionClampAction={{
                                        occurrenceId: occurrence.id,
                                        expanded: !!expandedInstructions[occurrence.id],
                                      }}
                                    >
                                      {occurrence.instructions}
                                    </p>
                                    {#if isCompactChecklist() && truncatedInstructions[occurrence.id]}
                                      <button
                                        type="button"
                                        class="instruction-toggle"
                                        onclick={() => toggleInstruction(occurrence.id)}
                                        >{expandedInstructions[occurrence.id]
                                          ? 'Show less'
                                          : 'Show more'}</button
                                      >
                                    {/if}
                                  {/if}
                                  {#if isCompactChecklist()}
                                    <div class="compact-details">
                                      <button
                                        type="button"
                                        class="mobile-notes-link"
                                        disabled={saving}
                                        onclick={(event) => {
                                          event.stopPropagation();
                                          openNotesPopover(occurrence);
                                        }}
                                        aria-expanded={notesPopover?.occurrence.id ===
                                          occurrence.id}
                                        >{occurrence.notes ? 'Notes' : 'Add notes…'}</button
                                      >
                                      {#if occurrence.resourceUrl || occurrence.estimatedDuration}
                                        <div class="compact-occurrence-meta">
                                          {#if occurrence.resourceUrl}
                                            <a
                                              href={occurrence.resourceUrl}
                                              target="_blank"
                                              rel="noreferrer">Open resource ↗</a
                                            >
                                          {/if}
                                          {#if occurrence.estimatedDuration}
                                            <span>{occurrence.estimatedDuration}</span>
                                          {/if}
                                        </div>
                                      {/if}
                                      {#if notesPopover?.occurrence.id === occurrence.id}
                                        <div
                                          class="notes-popover"
                                          onclick={(event) => event.stopPropagation()}
                                        >
                                          <label>
                                            <span class="sr-only">Notes for {occurrence.title}</span
                                            >
                                            <textarea
                                              rows="6"
                                              bind:value={notesPopover.draft}
                                              placeholder="Add notes…"
                                              disabled={saving}></textarea>
                                          </label>
                                          <div class="notes-popover-footer">
                                            <span>Saved when you close this editor</span>
                                            <button
                                              type="button"
                                              class="notes-cancel"
                                              onclick={() => (notesPopover = null)}
                                              disabled={saving}>Cancel</button
                                            >
                                          </div>
                                        </div>
                                      {/if}
                                    </div>
                                  {/if}
                                </div>
                                <div class="sheet-cell actions-cell" data-label="Actions">
                                  {#if occurrence.actions.length > 0}
                                    <ul
                                      class="occurrence-actions"
                                      aria-label={`Actions for ${occurrence.title}`}
                                    >
                                      {#each occurrence.actions as action (action.id)}
                                        <li>
                                          <label class="action-check">
                                            <input
                                              type="checkbox"
                                              aria-label={`Complete ${action.title}`}
                                              checked={action.completed}
                                              disabled={saving}
                                              onchange={(event) =>
                                                updateOccurrenceAction(
                                                  action,
                                                  (event.currentTarget as HTMLInputElement).checked,
                                                )}
                                            />
                                          </label>
                                          <span class:completed={action.completed}
                                            >{action.title}</span
                                          >
                                        </li>
                                      {/each}
                                    </ul>
                                  {:else}
                                    <span class="muted">—</span>
                                  {/if}
                                </div>
                                <div class="sheet-cell resource-cell" data-label="Resource">
                                  {#if occurrence.resourceUrl}
                                    <a
                                      href={occurrence.resourceUrl}
                                      target="_blank"
                                      rel="noreferrer">Open ↗</a
                                    >
                                  {:else}
                                    <span class="muted">—</span>
                                  {/if}
                                </div>
                                <div class="sheet-cell estimate-cell" data-label="Estimate">
                                  {occurrence.estimatedDuration || '—'}
                                </div>
                                <div class="sheet-cell status-cell" data-label="Status">
                                  <button
                                    type="button"
                                    class="status-badge status-{occurrence.status}"
                                    disabled={saving}
                                    onclick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        statusMenu.open &&
                                        statusMenu.occurrence?.id === occurrence.id
                                      ) {
                                        closeStatusMenu();
                                      } else {
                                        openStatusMenu(occurrence, e.currentTarget as HTMLElement);
                                      }
                                    }}
                                    aria-haspopup="listbox"
                                    aria-expanded={statusMenu.open &&
                                      statusMenu.occurrence?.id === occurrence.id}
                                  >
                                    <span class="status-dot"></span>
                                    {STATUS_LABELS[occurrence.status]}
                                    <span class="status-caret" aria-hidden="true">▾</span>
                                  </button>
                                  {#if occurrence.status !== 'done'}
                                    <button
                                      type="button"
                                      class="mark-done"
                                      title="Mark done"
                                      aria-label="Mark done"
                                      disabled={saving}
                                      onclick={() =>
                                        updateOccurrence(occurrence, { status: 'done' })}
                                    >
                                      <span class="mark-done-label">Mark done</span>
                                      <svg
                                        class="mark-done-icon"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                        focusable="false"
                                      >
                                        <path d="m5 12.5 4.5 4.5L19 7.5" />
                                      </svg>
                                    </button>
                                  {/if}
                                </div>
                                <div class="sheet-cell notes-cell" data-label="Notes">
                                  <button
                                    type="button"
                                    class="notes-preview"
                                    class:empty={!occurrence.notes}
                                    disabled={saving}
                                    onclick={(event) => {
                                      event.stopPropagation();
                                      openNotesPopover(occurrence);
                                    }}
                                    aria-expanded={notesPopover?.occurrence.id === occurrence.id}
                                    >{occurrence.notes || 'Add notes…'}</button
                                  >
                                  {#if !isCompactChecklist() && notesPopover?.occurrence.id === occurrence.id}
                                    <div
                                      class="notes-popover"
                                      onclick={(event) => event.stopPropagation()}
                                    >
                                      <label>
                                        <span class="sr-only">Notes for {occurrence.title}</span>
                                        <textarea
                                          rows="6"
                                          bind:value={notesPopover.draft}
                                          placeholder="Add notes…"
                                          disabled={saving}></textarea>
                                      </label>
                                      <div class="notes-popover-footer">
                                        <span>Saved when you close this editor</span>
                                        <button
                                          type="button"
                                          class="notes-cancel"
                                          onclick={() => (notesPopover = null)}
                                          disabled={saving}>Cancel</button
                                        >
                                      </div>
                                    </div>
                                  {/if}
                                </div>
                              </article>
                            {/each}
                          </div>
                        </section>
                      {/each}
                    </div>
                  {/if}
                </div>
              </section>
            {/each}
          </div>
        </section>
      {:else if activeTab === 'library'}
        <section class="library-view card">
          {#if !isMobile()}
            <div class="section-intro">
              <div>
                <h2>Your master checklist</h2>
                <p>
                  Edits update the current open occurrence; earlier periods retain their snapshot.
                </p>
              </div>
              <button class="primary" type="button" onclick={() => openCreateForm()}
                >+ Add item</button
              >
            </div>
          {/if}

          <div class="library-list">
            {#if activeItems.length === 0}
              <div class="empty-state">
                <h3>Start your master checklist</h3>
                <p>Add the periodic work you want to remember and reflect on.</p>
                <button class="primary" type="button" onclick={() => openCreateForm()}
                  >Add your first item</button
                >
              </div>
            {/if}
            {#each libraryGroups as cadenceGroup (cadenceGroup.cadence)}
              <section
                class="library-cadence-group"
                aria-labelledby={`library-${cadenceGroup.cadence}`}
              >
                <h3 id={`library-${cadenceGroup.cadence}`}>
                  {CADENCE_LABELS[cadenceGroup.cadence]}
                </h3>
                {#each cadenceGroup.categories as categoryGroup (categoryGroup.name)}
                  <section class="library-category-group">
                    <h4>
                      <span class="category-tag" style={tagStyle(categoryGroup.name)}
                        >{categoryGroup.name}</span
                      >
                    </h4>
                    {#each categoryGroup.items as item (item.id)}
                      <article
                        class="library-item"
                        class:dragging={draggedLibraryItemId === item.id}
                        ondragover={(event) => event.preventDefault()}
                        ondrop={(event) => {
                          event.preventDefault();
                          void reorderLibraryItems(item.id);
                        }}
                      >
                        <!-- Touch / phone: ⋯ → Move up / Move down instead of the drag handle.
                             Phones also move Edit / Archive / Delete into it. -->
                        {#if isMobile()}
                          <button
                            type="button"
                            class="row-menu-btn library-menu-btn"
                            aria-label={`Actions for ${item.title}`}
                            onclick={() => openLibraryItemSheet(item)}
                            ><Icon name="more" size={18} /></button
                          >
                        {:else if $tapAlternatives}
                          {#if categoryGroup.items.length > 1}
                            <button
                              type="button"
                              class="row-menu-btn library-menu-btn"
                              aria-label={`Reorder ${item.title}`}
                              onclick={() => openLibraryItemSheet(item)}
                              ><Icon name="more" size={18} /></button
                            >
                          {/if}
                        {:else}
                          <button
                            type="button"
                            class="drag-handle"
                            draggable="true"
                            aria-label={`Drag to reorder ${item.title}`}
                            title="Drag to reorder"
                            ondragstart={(event) => {
                              draggedLibraryItemId = item.id;
                              event.dataTransfer?.setData('text/plain', item.id);
                            }}
                            ondragend={() => (draggedLibraryItemId = null)}>⠿</button
                          >
                        {/if}
                        <div class="library-main">
                          <div class="library-copy">
                            <div class="library-meta">
                              {#if item.estimatedDuration}<span class="estimate"
                                  >{item.estimatedDuration}</span
                                >{/if}
                            </div>
                            <h3>{item.title}</h3>
                            {#if item.instructions}<p>{item.instructions}</p>{/if}
                            {#if item.resourceUrl}
                              <a href={item.resourceUrl} target="_blank" rel="noreferrer"
                                >Open resource ↗</a
                              >
                            {/if}
                          </div>
                          {#if item.actions.length > 0}
                            <section
                              class="library-action-list"
                              aria-label={`Actions for ${item.title}`}
                            >
                              <span class="library-action-list-label">Actions</span>
                              <ul>
                                {#each item.actions as action (action.id)}
                                  <li>
                                    <input
                                      type="checkbox"
                                      disabled
                                      aria-hidden="true"
                                      tabindex="-1"
                                    />
                                    <span>{action.title}</span>
                                  </li>
                                {/each}
                              </ul>
                            </section>
                          {/if}
                        </div>
                        {#if !isMobile()}
                          <div class="library-actions">
                            <button class="quiet" type="button" onclick={() => openEditForm(item)}
                              >Edit</button
                            >
                            <button class="quiet" type="button" onclick={() => toggleArchive(item)}
                              >Archive</button
                            >
                            <button
                              class="danger-link"
                              type="button"
                              onclick={() => (deleteConfirmation = item)}>Delete</button
                            >
                          </div>
                        {/if}
                      </article>
                    {/each}
                  </section>
                {/each}
              </section>
            {/each}
          </div>

          {#if archivedItems.length > 0}
            <section class="archived">
              <h3>Archived items</h3>
              {#each archivedItems as item (item.id)}
                <div>
                  <span>{item.title}</span>
                  <div class="library-actions">
                    <button class="quiet" type="button" onclick={() => toggleArchive(item)}
                      >Restore</button
                    ><button
                      class="danger-link"
                      type="button"
                      onclick={() => (deleteConfirmation = item)}>Delete</button
                    >
                  </div>
                </div>
              {/each}
            </section>
          {/if}
        </section>
      {:else}
        <section class="history-view card">
          {#if !isMobile()}
            <div class="section-intro">
              <div>
                <h2>History</h2>
                <p>
                  Review a past period as it stood, then correct status or notes when you need to
                  bookkeep.
                </p>
              </div>
            </div>
          {/if}
          <div class="history-toolbar">
            <DateRangeNav
              --drn-label-min-width="210px"
              start={historyRangeDates.start}
              end={historyRangeDates.end}
              label={historyRangeLabel}
              context={historyWeeksBehind > 0
                ? {
                    text: `${historyWeeksBehind} ${historyWeeksBehind === 1 ? 'week' : 'weeks'} prior`,
                    tone: 'prior',
                  }
                : undefined}
              previousLabel="Show earlier weeks"
              nextLabel="Show later weeks"
              nextDisabled={historyWeeksBehind === 0}
              onPrevious={() => navigateHistory(-historyWeekCount())}
              onNext={() => navigateHistory(historyWeekCount())}
              onToday={goHistoryToday}
            >
              {#snippet trailing()}
                {#if !isMobile()}
                  <label class="archived-toggle">
                    <input type="checkbox" bind:checked={showArchivedHistory} /> Show archived
                  </label>
                {/if}
              {/snippet}
            </DateRangeNav>
          </div>
          {#if historyGroups.length === 0}
            <div class="empty-state">
              <h3>No checklist history yet</h3>
              <p>
                Snapshots begin when a period is active. Future periods do not pre-fill history.
              </p>
            </div>
          {:else}
            <div class="history-board-wrap" style={`--week-columns: ${visibleHistoryWeeks.length}`}>
              <div class="sticky-head">
                <div class="head grid">
                  <div class="item-head">Item</div>
                  {#each visibleHistoryWeeks as week (week)}
                    <div class="date" class:today={week === currentWeekKey}>
                      <span>{historyWeekLabel(week)}</span>
                      <small>{week}</small>
                    </div>
                  {/each}
                </div>
              </div>
              <div class="board">
                {#each historyGroups as group (group.name)}
                  <section
                    class="group"
                    aria-label={`${group.name} history`}
                    style={`--group-color: ${CATEGORY_COLORS[group.color].text}`}
                  >
                    <div class="group-title">
                      <div>
                        <h2>
                          <span class="group-color-dot" aria-hidden="true"></span>{group.name}
                        </h2>
                      </div>
                    </div>
                    <div class="group-progress grid">
                      <div></div>
                      {#each visibleHistoryWeeks as week (week)}
                        {@const progress = historyGroupProgress(
                          group.cadences.flatMap((c) => c.entries),
                          week,
                        )}
                        <div>{progress.total ? `${progress.resolved}/${progress.total}` : ''}</div>
                      {/each}
                    </div>
                    {#each group.cadences as cadenceGroup (cadenceGroup.cadence)}
                      <div class="cadence-row">{CADENCE_LABELS[cadenceGroup.cadence]}</div>
                      {#each cadenceGroup.entries as entry (entry.item.id)}
                        <div class="history-row grid">
                          <div class="item-name">
                            {#if entry.latestOccurrence}
                              <button
                                type="button"
                                class="item-title"
                                disabled={saving}
                                title="Edit period notes"
                                onclick={() => openNotesPopover(entry.latestOccurrence!)}
                                aria-expanded={notesPopover?.occurrence.id ===
                                  entry.latestOccurrence?.id}>{entry.item.title}</button
                              >
                              {#if notesPopover?.occurrence.id === entry.latestOccurrence?.id}
                                <div
                                  class="notes-popover"
                                  onclick={(event) => event.stopPropagation()}
                                >
                                  <label>
                                    <span class="sr-only">Notes for {entry.item.title}</span>
                                    <textarea
                                      rows="6"
                                      bind:value={notesPopover.draft}
                                      placeholder="Add period notes…"
                                      disabled={saving}></textarea>
                                  </label>
                                  <div class="notes-popover-footer">
                                    <span>Saved when you close this editor</span>
                                    <button
                                      type="button"
                                      class="notes-cancel"
                                      onclick={() => (notesPopover = null)}
                                      disabled={saving}>Cancel</button
                                    >
                                  </div>
                                </div>
                              {/if}
                            {:else}
                              <span class="item-title static">{entry.item.title}</span>
                            {/if}
                          </div>
                          {#each visibleHistoryWeeks as week (week)}
                            {@const cell = historyCellFor(entry.item, week)}
                            <div class="cell" class:today={week === currentWeekKey}>
                              {#if cell.kind === 'status'}
                                <button
                                  class:completed={isHistoryCompleted(cell.occurrence.status)}
                                  class:missed={!isHistoryCompleted(cell.occurrence.status)}
                                  style={`--cell-color: ${CATEGORY_COLORS[group.color].text}; --completed-color: ${CATEGORY_COLORS[group.color].text}`}
                                  disabled={saving}
                                  aria-label={`${entry.item.title}, ${historyWeekLabel(week)}: ${STATUS_LABELS[cell.occurrence.status]}`}
                                  onclick={(event) => openHistoryStatusMenu(event, cell.occurrence)}
                                  oncontextmenu={(event) =>
                                    openHistoryStatusMenu(event, cell.occurrence)}
                                  onpointerdown={(event) =>
                                    historyCellPointerDown(event, cell.occurrence)}
                                  onpointermove={historyCellPointerMove}
                                  onpointerup={historyCellPointerEnd}
                                  onpointercancel={historyCellPointerEnd}
                                  onmouseenter={(event) =>
                                    showHistoryTooltip(
                                      historyCellTooltip(entry.item, cell),
                                      event.currentTarget as HTMLElement,
                                    )}
                                  onmouseleave={hideHistoryTooltip}
                                  onfocus={(event) =>
                                    showHistoryTooltip(
                                      historyCellTooltip(entry.item, cell),
                                      event.currentTarget as HTMLElement,
                                    )}
                                  onblur={hideHistoryTooltip}
                                >
                                  {#if isHistoryCompleted(cell.occurrence.status)}
                                    <svg
                                      class="cell-icon"
                                      viewBox="0 0 24 24"
                                      aria-hidden="true"
                                      focusable="false"
                                    >
                                      <path d="m5 12.5 4.5 4.5L19 7.5" />
                                    </svg>
                                  {:else}
                                    <svg
                                      class="cell-icon cell-icon-missed"
                                      viewBox="0 0 24 24"
                                      aria-hidden="true"
                                      focusable="false"
                                    >
                                      <path d="m7 7 10 10M17 7 7 17" />
                                    </svg>
                                  {/if}
                                </button>
                              {:else}
                                <div
                                  class="cell-blank"
                                  class:period-span={cell.kind === 'unrelatedPeriod'}
                                  aria-hidden="true"
                                  onmouseenter={(event) =>
                                    showHistoryTooltip(
                                      historyCellTooltip(entry.item, cell),
                                      event.currentTarget as HTMLElement,
                                    )}
                                  onmouseleave={hideHistoryTooltip}
                                ></div>
                              {/if}
                            </div>
                          {/each}
                        </div>
                      {/each}
                    {/each}
                  </section>
                {/each}
              </div>
            </div>
          {/if}
        </section>
      {/if}
    </div>
  </main>

  <!-- Floating status dropdown menu (rendered via portal-like fixed positioning) -->
  {#if statusMenu.open && statusMenu.occurrence}
    <div
      class="status-menu-popover"
      bind:this={statusMenuEl}
      style={statusMenuStyle}
      role="listbox"
      aria-label="Change status"
    >
      {#each Object.entries(STATUS_LABELS) as [value, label]}
        <button
          type="button"
          class="status-option status-{value}"
          class:active={statusMenu.occurrence?.status === value}
          onclick={() => pickStatus(value as CycleStatus)}
          role="option"
          aria-selected={statusMenu.occurrence?.status === value}
        >
          <span class="status-dot"></span>
          <span>{label}</span>
          {#if statusMenu.occurrence?.status === value}
            <span class="status-check" aria-hidden="true">✓</span>
          {/if}
        </button>
      {/each}
    </div>
  {/if}

  {#if historyTooltip}
    <span
      class="history-tooltip"
      class:wrap={historyTooltip.text.length > 40}
      style={`left: ${historyTooltip.x}px; top: ${historyTooltip.y}px`}
      role="tooltip">{historyTooltip.text}</span
    >
  {/if}

  {#if archiveConfirmation}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="modal-overlay"
      class:closing={archiveModal.closing}
      bind:this={archiveModal.overlayEl}
      onclick={() => archiveModal.animateClose()}
      role="presentation"
    >
      <div
        class="modal-card bulk-confirmation"
        onclick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="archive-confirmation-title"
        tabindex="-1"
      >
        <div class="modal-header">
          <h2 id="archive-confirmation-title">Archive checklist item?</h2>
          <button
            type="button"
            class="modal-close"
            onclick={() => archiveModal.animateClose()}
            aria-label="Close">×</button
          >
        </div>
        <div class="modal-body">
          <p>
            Archive <strong>{archiveConfirmation.title}</strong>? It will be removed from your
            current checklist and won't appear in future cycles.
          </p>
          <p class="modal-subtext">Past period records in history are kept.</p>
          <div class="modal-actions">
            <button
              type="button"
              class="quiet"
              onclick={() => archiveModal.animateClose()}
              disabled={saving}>Cancel</button
            >
            <button type="button" class="danger-confirm" onclick={confirmArchive} disabled={saving}
              >Archive item</button
            >
          </div>
        </div>
      </div>
    </div>
  {/if}

  {#if deleteConfirmation}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="modal-overlay"
      class:closing={deleteModal.closing}
      bind:this={deleteModal.overlayEl}
      onclick={() => deleteModal.animateClose()}
      role="presentation"
    >
      <div
        class="modal-card bulk-confirmation"
        onclick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-confirmation-title"
        tabindex="-1"
      >
        <div class="modal-header">
          <h2 id="delete-confirmation-title">Delete checklist item?</h2>
          <button
            type="button"
            class="modal-close"
            onclick={() => deleteModal.animateClose()}
            aria-label="Close">×</button
          >
        </div>
        <div class="modal-body">
          <p>
            Permanently delete <strong>{deleteConfirmation.title}</strong>? It will be removed from
            your library{#if !deleteConfirmation.archivedAt}, the current checklist,{/if} and every past
            period. This can't be undone.
          </p>
          {#if deletePastRecordCount === 1}
            <p class="modal-subtext">This also removes 1 past period record.</p>
          {:else if deletePastRecordCount > 1}
            <p class="modal-subtext">
              This also removes {deletePastRecordCount} past period records.
            </p>
          {:else if !deleteConfirmation.archivedAt}
            <p class="modal-subtext">
              It has no past period records yet, so only the current checklist is affected.
            </p>
          {:else}
            <p class="modal-subtext">
              It has no past period records yet; the only remaining record is this period's history
              snapshot.
            </p>
          {/if}
          <div class="modal-actions">
            <button
              type="button"
              class="quiet"
              onclick={() => deleteModal.animateClose()}
              disabled={saving}>Cancel</button
            >
            <button type="button" class="danger-confirm" onclick={confirmDelete} disabled={saving}
              >Delete item</button
            >
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- Category picker dropdown (item form) -->
  {#if categoryMenuOpen}
    <div
      class="category-menu-popover"
      bind:this={categoryMenuEl}
      style={categoryMenuStyle}
      role="listbox"
      aria-label="Choose a category"
    >
      {#if !categoryNewMode}
        {#each localCategories as category (category.id)}
          <button
            type="button"
            class="category-option"
            class:active={itemForm?.draft.category === category.name}
            onclick={() => pickCategory(category)}
            role="option"
            aria-selected={itemForm?.draft.category === category.name}
          >
            <span class="category-tag" style={tagStyle(category.name)}>{category.name}</span>
          </button>
        {/each}
        {#if currentCustomCategory}
          <button
            type="button"
            class="category-option custom-value"
            onclick={closeCategoryMenu}
            role="option"
            aria-selected={true}
          >
            <span class="category-tag" style={tagStyle(currentCustomCategory)}
              >{currentCustomCategory}</span
            >
            <span class="custom-hint">Current</span>
          </button>
        {/if}
        <button
          type="button"
          class="category-add-option"
          onclick={() => {
            categoryNewMode = true;
            categoryNewName = '';
          }}
        >
          <span class="add-icon" aria-hidden="true">+</span>
          Add new category…
        </button>
      {:else}
        <div class="category-new-form">
          <input
            autocomplete="off"
            value={categoryNewName}
            oninput={(e) => (categoryNewName = (e.currentTarget as HTMLInputElement).value)}
            maxlength="100"
            placeholder="Category name"
            aria-label="New category name"
          />
          <div class="category-swatches" role="group" aria-label="Category color">
            {#each CATEGORY_COLOR_ORDER as color (color)}
              <button
                type="button"
                class="category-swatch"
                class:selected={categoryNewColor === color}
                style={swatchStyle(color)}
                aria-label={`Color ${color}`}
                aria-pressed={categoryNewColor === color}
                onmouseenter={(e) => showColorTooltip(colorLabel(color), e.currentTarget)}
                onmouseleave={hideColorTooltip}
                onclick={() => (categoryNewColor = color)}
              ></button>
            {/each}
          </div>
          <div class="category-new-actions">
            <button type="button" class="quiet" onclick={() => (categoryNewMode = false)}
              >Back</button
            >
            <button
              type="button"
              class="primary"
              disabled={!categoryNewName.trim() || categorySaving}
              onclick={createCategoryFromPicker}>Add category</button
            >
          </div>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Manage categories modal -->
  {#if manageCategoriesOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="modal-overlay"
      class:closing={manageCategoriesModal.closing}
      bind:this={manageCategoriesModal.overlayEl}
      onclick={closeManageCategories}
      role="presentation"
    >
      <div
        class="modal-card"
        onclick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-categories-title"
        tabindex="-1"
      >
        <div class="modal-header">
          <h2 id="manage-categories-title">Manage categories</h2>
          <button
            type="button"
            class="modal-close"
            onclick={closeManageCategories}
            aria-label="Close">×</button
          >
        </div>
        <form
          class="modal-body"
          onsubmit={(e) => {
            e.preventDefault();
            saveManageCategories();
          }}
        >
          <p class="modal-subtext">
            Categories color your checklist tags. Renaming or recoloring updates every tag; deleting
            stops suggesting one but keeps existing items as they are. Changes apply when you save.
          </p>
          <div class="category-manage-list">
            {#each manageCategoryDrafts as category, index (category.id)}
              <div
                class="category-manage-row"
                class:dragging={draggedCategoryId === category.id}
                ondragover={(event) => event.preventDefault()}
                ondrop={(event) => {
                  event.preventDefault();
                  moveDraftCategory(category.id);
                }}
              >
                {#if $tapAlternatives}
                  {#if manageCategoryDrafts.length > 1}
                    <button
                      type="button"
                      class="row-menu-btn"
                      aria-label={`Reorder ${category.name}`}
                      onclick={() => openDraftCategorySheet(index)}
                      ><Icon name="more" size={18} /></button
                    >
                  {/if}
                {:else}
                  <button
                    type="button"
                    class="drag-handle"
                    draggable="true"
                    aria-label={`Drag to reorder ${category.name}`}
                    title="Drag to reorder"
                    ondragstart={(event) => {
                      draggedCategoryId = category.id;
                      event.dataTransfer?.setData('text/plain', category.id);
                    }}
                    ondragend={() => (draggedCategoryId = null)}>⠿</button
                  >
                {/if}
                <button
                  type="button"
                  class="category-dot-btn"
                  aria-label={`Color for ${category.name}`}
                  aria-haspopup="dialog"
                  aria-expanded={manageColorPopoverId === category.id}
                  onmouseenter={(e) =>
                    showColorTooltip(colorLabel(category.color), e.currentTarget)}
                  onmouseleave={hideColorTooltip}
                  onclick={(e) =>
                    manageColorPopoverId === category.id
                      ? closeManageColorPopover()
                      : openManageColorPopover(category.id, e.currentTarget as HTMLElement)}
                >
                  <span class="category-dot" style={swatchStyle(category.color)}></span>
                </button>
                <input
                  autocomplete="off"
                  bind:value={category.name}
                  maxlength="100"
                  aria-label="Category name"
                />
                <button
                  type="button"
                  class="danger-link manage-action-btn"
                  disabled={manageCategorySaving}
                  onclick={() => requestRemoveManageCategory(category)}>Delete</button
                >
              </div>
            {/each}
          </div>
          <div class="category-manage-add">
            <button
              type="button"
              class="category-dot-btn"
              aria-label="Choose a color for the new category"
              aria-haspopup="dialog"
              aria-expanded={manageColorPopoverId === 'new'}
              onmouseenter={(e) =>
                showColorTooltip(
                  newManageCategoryColor ? colorLabel(newManageCategoryColor) : 'Pick a color',
                  e.currentTarget,
                )}
              onmouseleave={hideColorTooltip}
              onclick={(e) =>
                manageColorPopoverId === 'new'
                  ? closeManageColorPopover()
                  : openManageColorPopover('new', e.currentTarget as HTMLElement)}
            >
              <span
                class="category-dot"
                class:placeholder={!newManageCategoryColor}
                style={newManageCategoryColor ? swatchStyle(newManageCategoryColor) : undefined}
              ></span>
            </button>
            <input
              autocomplete="off"
              bind:value={newManageCategoryName}
              maxlength="100"
              placeholder="New category name"
              aria-label="New category name"
            />
          </div>
          <div class="modal-actions">
            <button type="button" class="quiet" onclick={closeManageCategories}>Cancel</button>
            <button
              type="submit"
              class="primary"
              disabled={!hasPersistableManageChanges ||
                manageCategorySaving ||
                manageCategoryDrafts.some((draft) => !draft.name.trim())}>Save categories</button
            >
          </div>
        </form>
      </div>
    </div>
  {/if}

  <!-- Manage categories: color picker popover -->
  {#if manageColorPopoverId !== null}
    <div
      class="category-menu-popover color-picker-popover"
      bind:this={manageColorEl}
      style={manageColorStyle}
      role="dialog"
      aria-label="Choose a color"
    >
      <div class="category-swatches" role="group" aria-label="Category color">
        {#each CATEGORY_COLOR_ORDER as color (color)}
          <button
            type="button"
            class="category-swatch"
            class:selected={manageColorPopoverSelected === color}
            style={swatchStyle(color)}
            aria-label={`Color ${color}`}
            onmouseenter={(e) => showColorTooltip(colorLabel(color), e.currentTarget)}
            onmouseleave={hideColorTooltip}
            onclick={() => pickManageColor(color)}
          ></button>
        {/each}
      </div>
    </div>
  {/if}

  <!-- Manage categories: delete confirmation -->
  {#if manageDeleteConfirmation}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="modal-overlay"
      class:closing={manageDeleteModal.closing}
      bind:this={manageDeleteModal.overlayEl}
      onclick={() => manageDeleteModal.animateClose()}
      role="presentation"
    >
      <div
        class="modal-card bulk-confirmation"
        onclick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-category-title"
        tabindex="-1"
      >
        <div class="modal-header">
          <h2 id="delete-category-title">Delete category?</h2>
          <button
            type="button"
            class="modal-close"
            onclick={() => manageDeleteModal.animateClose()}
            aria-label="Close">×</button
          >
        </div>
        <div class="modal-body">
          <p>
            Delete <strong>{manageDeleteConfirmation.name}</strong>? Existing items keep their
            category text; it just stops being suggested and its color is removed.
          </p>
          <p class="modal-subtext">Historical snapshots are not changed.</p>
          <div class="modal-actions">
            <button
              type="button"
              class="quiet"
              onclick={() => manageDeleteModal.animateClose()}
              disabled={manageCategorySaving}>Cancel</button
            >
            <button
              type="button"
              class="danger-confirm"
              onclick={confirmRemoveManageCategory}
              disabled={manageCategorySaving}>Delete category</button
            >
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- Manage categories: discard unsaved changes -->
  {#if manageDiscardConfirmation}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="modal-overlay discard-confirmation"
      class:closing={manageDiscardModal.closing}
      bind:this={manageDiscardModal.overlayEl}
      onclick={() => manageDiscardModal.animateClose()}
      role="presentation"
    >
      <div
        class="modal-card discard-modal-card"
        onclick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="discard-category-changes-title"
        tabindex="-1"
      >
        <div class="modal-header">
          <h2 id="discard-category-changes-title">Discard unsaved changes?</h2>
        </div>
        <div class="modal-body">
          <p class="modal-subtext">Your category edits will be lost.</p>
          <div class="modal-actions">
            <button type="button" class="quiet" onclick={() => manageDiscardModal.animateClose()}
              >Keep editing</button
            >
            <button type="button" class="danger-confirm" onclick={discardManageCategoryChanges}
              >Discard changes</button
            >
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- Discard item form changes confirmation -->
  {#if itemDiscardConfirmation}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="modal-overlay discard-confirmation"
      class:closing={itemDiscardModal.closing}
      bind:this={itemDiscardModal.overlayEl}
      onclick={() => itemDiscardModal.animateClose()}
      role="presentation"
    >
      <div
        class="modal-card discard-modal-card"
        onclick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="discard-item-changes-title"
        tabindex="-1"
      >
        <div class="modal-header">
          <h2 id="discard-item-changes-title">Discard unsaved changes?</h2>
        </div>
        <div class="modal-body">
          <p class="modal-subtext">
            Your edits to this checklist item and its actions will be lost.
          </p>
          <div class="modal-actions">
            <button type="button" class="quiet" onclick={() => itemDiscardModal.animateClose()}
              >Keep editing</button
            >
            <button type="button" class="danger-confirm" onclick={discardItemChanges}
              >Discard changes</button
            >
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- Discard nudge settings changes confirmation -->
  {#if nudgeDiscardConfirmation}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="modal-overlay discard-confirmation"
      class:closing={nudgeDiscardModal.closing}
      bind:this={nudgeDiscardModal.overlayEl}
      onclick={() => nudgeDiscardModal.animateClose()}
      role="presentation"
    >
      <div
        class="modal-card discard-modal-card"
        onclick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="discard-nudge-changes-title"
        tabindex="-1"
      >
        <div class="modal-header">
          <h2 id="discard-nudge-changes-title">Discard unsaved changes?</h2>
        </div>
        <div class="modal-body">
          <p class="modal-subtext">Your cycle nudge settings changes will be lost.</p>
          <div class="modal-actions">
            <button type="button" class="quiet" onclick={() => nudgeDiscardModal.animateClose()}
              >Keep editing</button
            >
            <button type="button" class="danger-confirm" onclick={discardNudgeChanges}
              >Discard changes</button
            >
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- Cycle nudge settings modal -->
  {#if nudgeSettingsOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="modal-overlay"
      class:closing={nudgeModal.closing}
      bind:this={nudgeModal.overlayEl}
      onclick={closeNudgeSettings}
      role="presentation"
    >
      <div
        class="modal-card nudge-settings-modal"
        onclick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="nudge-settings-title"
        tabindex="-1"
      >
        <div class="modal-header">
          <h2 id="nudge-settings-title">Nudge settings</h2>
          <button type="button" class="modal-close" onclick={closeNudgeSettings} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p class="modal-subtext">
            Get a gentle reminder when unfinished items are nearing the finish line.
          </p>
          <label class="nudge-toggle">
            <input type="checkbox" bind:checked={nudgeDraft.enabled} />
            <span>Show end-of-period nudges</span>
          </label>
          <label class="nudge-toggle">
            <input type="checkbox" bind:checked={nudgeDraft.highlight_items} />
            <span>Highlight unfinished items in the checklist</span>
          </label>
          <div class="nudge-thresholds modal-nudge-thresholds">
            {#each CADENCES as cadence}
              <label>
                <span>{CADENCE_LABELS[cadence]} reminder</span>
                <span class="threshold-input"
                  ><input
                    autocomplete="off"
                    type="number"
                    min="0"
                    max="365"
                    bind:value={nudgeDraft.thresholds[cadence]}
                  /> days before</span
                >
              </label>
            {/each}
          </div>
          <div class="modal-actions">
            <button type="button" class="quiet" onclick={closeNudgeSettings} disabled={nudgeSaving}
              >Cancel</button
            >
            <button
              type="button"
              class="primary"
              disabled={nudgeSaving}
              onclick={saveNudgePreferences}>Save settings</button
            >
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- Item form modal (create + edit) -->
  {#if itemForm}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div
      class="modal-overlay"
      class:closing={itemModal.closing}
      bind:this={itemModal.overlayEl}
      onclick={closeItemModal}
      role="presentation"
    >
      <div class="modal-card" onclick={(e) => e.stopPropagation()} role="dialog" tabindex="-1">
        <div class="modal-header">
          <h2>
            {itemForm.mode === 'create' ? 'New checklist item' : 'Edit checklist item'}
          </h2>
          <button type="button" class="modal-close" onclick={closeItemModal} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
        <form class="modal-body" onsubmit={submitItemForm}>
          <label class="modal-field">
            <span>Title</span>
            <input
              autocomplete="off"
              value={itemForm.draft.title}
              oninput={(e) => (itemForm!.draft.title = (e.currentTarget as HTMLInputElement).value)}
              required
              maxlength="250"
              placeholder="e.g. Review monthly finances"
            />
          </label>
          <div class="modal-row">
            <div class="modal-field">
              <span>Category</span>
              <button
                type="button"
                class="category-picker-trigger"
                onclick={(e) =>
                  categoryMenuOpen
                    ? closeCategoryMenu()
                    : openCategoryMenu(e.currentTarget as HTMLElement)}
                aria-haspopup="listbox"
                aria-expanded={categoryMenuOpen}
              >
                <span class="category-tag" style={tagStyle(itemForm.draft.category)}
                  >{itemForm.draft.category}</span
                >
                <span class="status-caret" aria-hidden="true">▾</span>
              </button>
            </div>
            <label class="modal-field">
              <span>Cadence</span>
              <select
                value={itemForm.draft.cadence}
                onchange={(e) =>
                  (itemForm!.draft.cadence = (e.currentTarget as HTMLSelectElement)
                    .value as CycleCadence)}
              >
                {#each CADENCES as cadence}
                  <option value={cadence}>{CADENCE_LABELS[cadence]}</option>
                {/each}
              </select>
            </label>
          </div>
          <label class="modal-field">
            <span>Estimated duration</span>
            <input
              autocomplete="off"
              value={itemForm.draft.estimated_duration}
              oninput={(e) =>
                (itemForm!.draft.estimated_duration = (e.currentTarget as HTMLInputElement).value)}
              maxlength="100"
              placeholder="15 min, 1 hour…"
            />
          </label>
          <label class="modal-field">
            <span>Resource URL</span>
            <input
              autocomplete="url"
              value={itemForm.draft.resource_url}
              oninput={(e) =>
                (itemForm!.draft.resource_url = (e.currentTarget as HTMLInputElement).value)}
              type="url"
              placeholder="https://…"
            />
          </label>
          <label class="modal-field">
            <span>Instructions</span>
            <textarea
              value={itemForm.draft.instructions}
              oninput={(e) =>
                (itemForm!.draft.instructions = (e.currentTarget as HTMLTextAreaElement).value)}
              rows="4"
              placeholder="Helpful steps, links, and context for future you…"></textarea>
          </label>
          <fieldset class="item-actions-editor">
            <legend>Actions <span>(optional)</span></legend>
            <p>
              Checking an action starts an unstarted item; otherwise actions do not change its
              status.
            </p>
            {#each itemForm.draft.actions as action, index (action.id ?? index)}
              <div
                class="item-action-draft"
                class:dragging={draggedActionIndex === index}
                ondragover={(event) => event.preventDefault()}
                ondrop={(event) => {
                  event.preventDefault();
                  reorderDraftAction(index);
                }}
              >
                {#if $tapAlternatives}
                  {#if itemForm.draft.actions.length > 1}
                    <button
                      type="button"
                      class="row-menu-btn"
                      aria-label={`Reorder action ${index + 1}`}
                      onclick={() => openDraftActionSheet(index)}
                      ><Icon name="more" size={18} /></button
                    >
                  {/if}
                {:else}
                  <button
                    type="button"
                    class="drag-handle"
                    draggable="true"
                    aria-label={`Drag to reorder action ${index + 1}`}
                    title="Drag to reorder"
                    ondragstart={(event) => {
                      draggedActionIndex = index;
                      event.dataTransfer?.setData('text/plain', String(index));
                    }}
                    ondragend={() => (draggedActionIndex = null)}>⠿</button
                  >
                {/if}
                <input
                  autocomplete="off"
                  aria-label="Action title"
                  value={action.title}
                  maxlength="250"
                  placeholder="e.g. Gather account statements"
                  oninput={(event) =>
                    (itemForm!.draft.actions[index].title = (
                      event.currentTarget as HTMLInputElement
                    ).value)}
                />
                <button
                  type="button"
                  class="remove-action"
                  aria-label={`Remove action ${index + 1}`}
                  title="Remove action"
                  onclick={() => itemForm!.draft.actions.splice(index, 1)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M3 6h18M9 6V4h6v2m-8 0 1 14h8l1-14M10 10v6m4-6v6"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>Remove</span>
                </button>
              </div>
            {/each}
            <button
              type="button"
              class="quiet add-action"
              onclick={() => itemForm!.draft.actions.push({ title: '' })}>+ Add action</button
            >
          </fieldset>
          <div class="modal-actions">
            <button type="button" class="quiet" onclick={closeItemModal} disabled={saving}>
              Cancel
            </button>
            <button type="submit" class="primary" disabled={saving}>
              {itemForm.mode === 'create' ? 'Add to cycles' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  {/if}

  <!-- App-style color tooltip (fixed-position, above the hovered element) -->
  {#if colorTooltipText && colorTooltipPosition}
    <span
      class="cat-tooltip"
      style={`left: ${colorTooltipPosition.x}px; top: ${colorTooltipPosition.y}px`}
      role="tooltip">{colorTooltipText}</span
    >
  {/if}
</div>

{#if $tapAlternatives}
  <!-- Above the page's modals: it also opens from Manage categories and the item form -->
  <ActionSheet
    --mobile-sheet-z="300"
    open={rowSheetOpen}
    onClose={() => (rowSheetOpen = false)}
    title={rowSheet?.title}
    actions={rowSheet?.actions ?? []}
  />
{/if}

<style>
  :global(*) {
    box-sizing: border-box;
  }

  .cycles-page {
    min-height: 100vh;
    background: #f9fafb;
    color: #1f2937;
  }

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 1rem;
    background: white;
    border-bottom: 1px solid #e5e7eb;
  }

  .header-left,
  .tabs,
  .form-actions,
  .library-meta,
  .library-actions,
  .archived > div {
    display: flex;
    align-items: center;
  }

  .header-left {
    gap: 0.75rem;
  }

  :global(.logo) {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    text-decoration: none;
    color: #1f2937;
  }

  .logo-icon {
    width: 1.25rem;
    height: 1.25rem;
  }

  .logo-text {
    font-size: 0.8125rem;
    font-weight: 700;
  }

  .page-body {
    padding: 1.5rem 1rem;
  }

  .content-container {
    max-width: 78rem;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .page-intro {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .page-intro-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .settings-menu-wrap {
    position: relative;
  }

  .settings-trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  .settings-chevron {
    width: 0.625rem;
    height: 0.375rem;
    margin-left: 0.125rem;
  }

  .settings-menu {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 10;
    min-width: 11rem;
    padding: 0.25rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity 0.15s ease,
      visibility 0.15s ease;
  }

  /* Keep the menu open while moving the pointer from the trigger to the menu. */
  .settings-menu::before {
    content: '';
    position: absolute;
    top: -0.5rem;
    left: 0;
    right: 0;
    height: 0.5rem;
  }

  .settings-menu-wrap:hover .settings-menu,
  .settings-menu.open {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  .settings-menu button {
    display: block;
    width: 100%;
    padding: 0.5rem 0.625rem;
    color: #374151;
    text-align: left;
    background: transparent;
    border: 0;
    border-radius: 0.25rem;
    font-size: 0.75rem;
  }

  .settings-menu button:hover {
    background: #f3f4f6;
  }

  .page-intro h1 {
    margin: 0;
    color: #111827;
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.3;
  }

  .hero-copy {
    max-width: 720px;
    margin: 0.25rem 0 0;
    color: #6b7280;
    font-size: 0.75rem;
    line-height: 1.5;
  }

  button,
  input,
  textarea,
  select {
    font: inherit;
  }

  button {
    cursor: pointer;
  }

  /* ─── Cards (shared surface style used elsewhere in the app) ─── */
  .card {
    background: white;
    border-radius: 0.5rem;
    padding: 1rem 1.125rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  /* ─── Tabs (match Projects/Index pill style) ─── */
  .tabs {
    gap: 0.25rem;
  }

  .tabs :global(.tab) {
    padding: 0.375rem 0.625rem;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.15s;
  }

  .tabs :global(.tab):hover {
    color: #374151;
    background: #f3f4f6;
  }

  .tabs :global(.tab.active) {
    color: #1f2937;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  }

  .tab-count {
    font-size: 0.625rem;
    color: #9ca3af;
    margin-left: 0.125rem;
  }

  /* ─── Buttons ─── */
  .primary,
  .quiet,
  .danger-link {
    padding: 0.375rem 0.75rem;
    border: 1px solid transparent;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    transition: all 0.15s;
  }

  .primary {
    color: white;
    background: #3b82f6;
  }

  .primary:hover:not(:disabled) {
    background: #2563eb;
  }

  .primary:disabled {
    cursor: wait;
    opacity: 0.6;
  }

  .quiet,
  .content-container :global(.quiet) {
    color: #374151;
    text-decoration: none;
    background: white;
    border-color: #d1d5db;
  }

  .quiet:hover,
  .content-container :global(.quiet):hover {
    background: #f3f4f6;
  }

  .manage-library-link :global(a) {
    display: inline-block;
    padding: 0.375rem 0.75rem;
    color: #374151;
    text-decoration: none;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
    white-space: nowrap;
    transition: all 0.15s;
  }

  .manage-library-link :global(a):hover {
    background: #f3f4f6;
  }

  .danger-link {
    color: #b91c1c;
    background: transparent;
  }

  .danger-link:hover {
    background: #fef2f2;
  }

  /* ─── Section headings (compact like other pages) ─── */
  .workspace-heading,
  .section-intro {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.625rem;
  }

  .workspace-heading h2,
  .section-intro h2 {
    margin: 0;
    color: #111827;
    font-size: 0.9375rem;
    font-weight: 700;
  }

  .workspace-heading p,
  .section-intro p {
    margin: 0.125rem 0 0;
    color: #6b7280;
    font-size: 0.75rem;
    line-height: 1.45;
  }

  .cycle-nudge {
    position: sticky;
    top: 0.5rem;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 0.875rem;
    color: #713f12;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 0.5rem;
    box-shadow: 0 3px 8px rgba(120, 53, 15, 0.08);
  }

  .nudge-spark {
    font-size: 1.25rem;
    color: #d97706;
  }

  .occurrence.nudge-highlight {
    box-shadow: inset 3px 0 0 #f59e0b;
  }

  .occurrence.nudge-highlight .checklist-cell h4 {
    color: #92400e;
  }

  .nudge-item-spark {
    display: inline-block;
    margin-right: 0.3rem;
    color: #d97706;
    font-size: 0.8rem;
    animation: nudge-sparkle 2.2s ease-in-out infinite;
  }

  @keyframes nudge-sparkle {
    0%,
    100% {
      opacity: 0.55;
      transform: scale(0.9) rotate(0deg);
    }
    50% {
      opacity: 1;
      transform: scale(1.15) rotate(12deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .nudge-item-spark {
      animation: none;
    }
  }
  .nudge-copy {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.125rem;
    font-size: 0.75rem;
  }
  .nudge-copy strong {
    font-size: 0.8125rem;
  }
  .nudge-copy span {
    color: #92400e;
  }
  .nudge-close {
    padding: 0.25rem 0.5rem;
    color: #92400e;
    background: transparent;
    border: 0;
    font-size: 0.6875rem;
    font-weight: 600;
  }

  .nudge-settings-modal {
    max-width: 34rem;
  }

  /* Phones: one line, items per cadence and the nearest deadline. */
  .cycle-nudge.slim {
    position: static;
    gap: 0.5rem;
    min-height: 2.75rem;
    padding: 0 0.25rem 0 0.75rem;
    box-shadow: none;
  }

  .cycle-nudge.slim .nudge-spark {
    font-size: 1rem;
  }

  .nudge-slim-copy {
    flex: 1;
    min-width: 0;
    margin: 0;
    overflow: hidden;
    color: #92400e;
    font-size: 0.8125rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .cycle-nudge.slim .nudge-close {
    min-height: 2.75rem;
    padding: 0 0.625rem;
    font-size: 0.8125rem;
  }

  .modal-nudge-thresholds {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    margin-top: 1rem;
  }

  .modal-nudge-thresholds label {
    justify-content: space-between;
  }
  .nudge-toggle {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: #374151;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .nudge-thresholds {
    display: flex;
    flex: 1 1 100%;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
  }
  .nudge-thresholds label {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: #4b5563;
    font-size: 0.6875rem;
  }
  .threshold-input {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }
  .threshold-input input {
    width: 3.25rem;
    padding: 0.25rem;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    font-size: 0.6875rem;
  }
  .nudge-settings-actions {
    display: flex;
    gap: 0.5rem;
    margin-left: auto;
  }

  /* ─── Per-cadence tables ─── */
  .cadence-tables {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .cadence-table {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    overflow: visible;
  }

  .cadence-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.625rem 0.875rem;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
  }

  .cadence-header-actions {
    display: flex;
    align-items: center;
    gap: 0.625rem;
  }

  .section-add {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.5rem;
    color: #6b7280;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 0.375rem;
    font-size: 0.6875rem;
    font-weight: 600;
    cursor: pointer;
    opacity: 0;
    transition:
      opacity 0.15s,
      color 0.15s,
      border-color 0.15s,
      background 0.15s;
  }

  .cadence-table:hover .section-add,
  .cadence-table:focus-within .section-add,
  .section-add:focus-visible {
    opacity: 1;
  }

  .section-add:hover {
    color: #2563eb;
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  .section-add .add-icon {
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1;
  }

  @media (hover: none) {
    .section-add {
      opacity: 1;
    }
  }

  .cadence-heading {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.5rem;
  }

  .cadence-heading h3 {
    min-width: 0;
    margin: 0;
    color: #1e293b;
    font-size: 0.8125rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .period-pill {
    flex: none;
    padding: 0.125rem 0.4375rem;
    color: #475569;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    font-weight: 600;
  }

  .cadence-heading-range {
    font-weight: 600;
    letter-spacing: normal;
    text-transform: none;
  }

  .period-range {
    color: #64748b;
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .cadence-progress {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .progress {
    color: #6b7280;
    font-size: 0.6875rem;
    white-space: nowrap;
  }

  .progress-track {
    width: 8rem;
    height: 0.375rem;
    overflow: hidden;
    background: #e5e7eb;
    border-radius: 999px;
  }

  .progress-fill {
    display: block;
    height: 100%;
    background: #22c55e;
    border-radius: inherit;
    transition: width 0.2s ease;
  }

  .table-wrap {
    overflow: visible;
  }

  /* ─── Compact checklist sheet, grouped by category ─── */
  .sheet-row {
    display: grid;
    grid-template-columns:
      minmax(220px, 2.2fr) minmax(180px, 1.4fr) minmax(80px, 0.6fr) minmax(80px, 0.6fr)
      minmax(155px, 1.1fr) minmax(160px, 1.2fr);
    align-items: center;
  }

  .sheet-column-header {
    color: #6b7280;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .sheet-column-header span {
    padding: 0.375rem 0.625rem;
    border-right: 1px solid #e5e7eb;
  }

  .sheet-column-header span:last-child {
    border-right: 0;
  }

  .category-divider {
    padding: 0.4375rem 0.875rem;
    background: color-mix(in srgb, var(--cat-bg, #f1f5f9) 42%, white);
    border-top: 1px solid color-mix(in srgb, var(--cat-border, #e2e8f0) 52%, white);
    border-bottom: 1px solid color-mix(in srgb, var(--cat-border, #e2e8f0) 52%, white);
  }

  .category-divider h4 {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin: 0;
    color: var(--cat-text, #475569);
    font-size: 0.75rem;
    line-height: 1.25;
  }

  .category-color-dot {
    width: 0.5rem;
    height: 0.5rem;
    flex: none;
    border-radius: 999px;
    background: var(--cat-text, #475569);
  }

  .category-group-name {
    font-weight: 700;
  }

  .category-group-progress {
    color: color-mix(in srgb, var(--cat-text, #475569) 70%, #64748b);
    font-size: 0.6875rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .sheet-category-items .occurrence + .occurrence {
    border-top: 1px solid #f3f4f6;
  }

  .occurrence {
    min-width: 0;
    transition: background 0.1s;
  }

  .occurrence:hover {
    background: #f9fafb;
  }

  .occurrence[data-tone='done'] {
    background: #f7fdf8;
  }

  .occurrence[data-tone='done'] .checklist-cell h4 {
    color: #166534;
    text-decoration: line-through;
    text-decoration-color: rgba(22, 101, 52, 0.4);
  }

  .occurrence[data-tone='skipped'],
  .occurrence[data-tone='not_applicable'] {
    color: #6b7280;
  }

  .occurrence[data-tone='skipped'] .checklist-cell h4,
  .occurrence[data-tone='not_applicable'] .checklist-cell h4 {
    color: #6b7280;
    text-decoration: line-through;
  }

  .sheet-cell {
    min-width: 0;
    padding: 0.5rem 0.625rem;
    border-right: 1px solid #f3f4f6;
    color: #374151;
    font-size: 0.75rem;
  }

  .sheet-cell:last-child {
    border-right: 0;
  }

  .estimate-cell {
    color: #6b7280;
  }

  .mobile-details-cell {
    display: none;
  }

  .category-tag {
    display: inline-block;
    padding: 0.0625rem 0.375rem;
    color: var(--cat-text, #475569);
    background: var(--cat-bg, #f1f5f9);
    box-shadow: inset 0 0 0 1px var(--cat-border, transparent);
    border-radius: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 500;
  }

  .checklist-cell h4 {
    margin: 0;
    color: #1f2937;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.35;
  }

  .checklist-title {
    padding: 0;
    color: inherit;
    background: none;
    border: 0;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .checklist-title:hover {
    color: #2563eb;
    text-decoration: underline;
  }

  .checklist-title:disabled {
    cursor: default;
  }

  .checklist-cell .checklist-instructions {
    margin: 0.25rem 0 0;
    color: #4b5563;
    font-size: 0.6875rem;
    line-height: 1.45;
    white-space: pre-line;
  }

  .occurrence-actions {
    display: grid;
    gap: 0.25rem;
    margin: 0.375rem 0 0;
    padding: 0;
    list-style: none;
  }

  .occurrence-actions li {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: #475569;
    font-size: 0.75rem;
  }

  /* Wraps only the checkbox: on phones it is the 44px hit area (see the
     max-width: 767px block); elsewhere it adds nothing. */
  .action-check {
    display: contents;
  }

  .occurrence-actions input {
    width: 0.875rem;
    height: 0.875rem;
    margin: 0;
    accent-color: #16a34a;
  }

  .occurrence-actions .completed {
    color: #64748b;
    text-decoration: line-through;
  }

  .resource-cell a,
  .library-copy a {
    color: #2563eb;
    font-size: 0.75rem;
    font-weight: 600;
    text-decoration: none;
  }

  .resource-cell a:hover,
  .library-copy a:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }

  .muted {
    color: #9ca3af;
  }

  .status-cell {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem;
  }

  .mark-done {
    padding: 0.1875rem 0.375rem;
    color: #166534;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    font-weight: 700;
  }

  .mark-done:hover:not(:disabled) {
    background: #dcfce7;
  }

  .mark-done-icon {
    display: none;
  }

  .notes-cell {
    position: relative;
  }

  .notes-preview {
    display: block;
    width: 100%;
    overflow: hidden;
    padding: 0.3125rem 0.4rem;
    color: #374151;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    font-size: 0.75rem;
  }

  .notes-preview:hover:not(:disabled),
  .notes-preview:focus-visible {
    background: white;
    border-color: #93c5fd;
    outline: none;
  }

  .notes-preview.empty {
    color: #9ca3af;
    font-style: italic;
  }

  .notes-popover {
    position: absolute;
    z-index: 40;
    top: calc(100% + 0.25rem);
    right: 0;
    width: min(24rem, 80vw);
    padding: 0.625rem;
    background: white;
    border: 1px solid #bfdbfe;
    border-radius: 0.375rem;
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.14);
  }

  .notes-popover textarea {
    display: block;
    width: 100%;
    min-height: 1.625rem;
    padding: 0.5rem 0.625rem;
    color: #374151;
    resize: vertical;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    line-height: 1.45;
  }

  .notes-popover textarea:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }

  .notes-popover-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: 0.375rem;
    color: #6b7280;
    font-size: 0.625rem;
  }

  .notes-cancel {
    padding: 0.1875rem 0.375rem;
    color: #4b5563;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    font-weight: 600;
  }

  .notes-cancel:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .item-actions-editor {
    /* Fieldsets default to min-content width, which overflowed phone-width forms */
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
  }

  .item-actions-editor legend {
    color: #374151;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .item-actions-editor legend span,
  .item-actions-editor > p,
  .library-actions-summary {
    color: #6b7280;
    font-size: 0.6875rem;
  }

  .item-actions-editor > p {
    margin: 0.25rem 0 0.5rem;
  }

  .drag-handle {
    align-self: center;
    padding: 0.125rem 0.25rem;
    color: #94a3b8;
    background: transparent;
    border: 0;
    border-radius: 0.25rem;
    cursor: grab;
    font-size: 1rem;
    line-height: 1;
  }

  .drag-handle:active {
    cursor: grabbing;
  }

  /* Touch / phone stand-in for .drag-handle: ⋯ → Move up / Move down (32px wide, 44px hit area) */
  .row-menu-btn {
    position: relative;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;
    width: 2rem;
    height: var(--mobile-tap);
    padding: 0;
    color: #64748b;
    background: transparent;
    border: 0;
    border-radius: 0.375rem;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .row-menu-btn::after {
    content: '';
    position: absolute;
    inset: 0 -6px;
  }

  .row-menu-btn:active {
    background: #f1f5f9;
  }

  .dragging {
    opacity: 0.5;
  }

  .item-action-draft {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.375rem;
  }

  .item-action-draft input {
    min-width: 0;
    flex: 1;
    padding: 0.5rem 0.625rem;
    color: #1f2937;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 400;
    transition: border-color 0.15s;
  }

  .item-action-draft input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  }

  .remove-action,
  .add-action {
    font-size: 0.6875rem;
  }

  .remove-action {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem;
    color: #b91c1c;
    background: #fff1f2;
    border: 1px solid #fecdd3;
    border-radius: 0.375rem;
    font-weight: 600;
  }

  .remove-action:hover:not(:disabled) {
    color: #991b1b;
    background: #ffe4e6;
    border-color: #fda4af;
  }

  .remove-action svg {
    flex: 0 0 auto;
  }

  .add-action {
    margin-top: 0.5rem;
  }

  .library-actions-summary {
    margin: 0.375rem 0 0;
    font-weight: 600;
  }

  .danger-confirm {
    padding: 0.375rem 0.75rem;
    color: white;
    background: #b91c1c;
    border: 1px solid #991b1b;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .danger-confirm:hover:not(:disabled) {
    background: #991b1b;
  }

  .danger-confirm:disabled {
    cursor: wait;
    opacity: 0.6;
  }

  /* ─── Status badge (clickable dropdown) ─── */
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem 0.25rem 0.4375rem;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    transition: all 0.12s;
    text-align: left;
    white-space: nowrap;
    font-family: inherit;
  }

  .status-badge:hover {
    filter: brightness(0.97);
  }

  .status-badge:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  .status-dot {
    width: 0.4375rem;
    height: 0.4375rem;
    border-radius: 999px;
    background: currentColor;
    opacity: 0.85;
    flex-shrink: 0;
  }

  .status-caret {
    font-size: 0.5625rem;
    opacity: 0.6;
    margin-left: 0.0625rem;
  }

  .status-badge.status-unstarted,
  .status-option.status-unstarted {
    --status-color: #1e40af;
    --status-bg: #dbeafe;
    --status-border: #bfdbfe;
  }
  .status-badge.status-in_progress,
  .status-option.status-in_progress {
    --status-color: #c2410c;
    --status-bg: #ffedd5;
    --status-border: #fed7aa;
  }
  .status-badge.status-done,
  .status-option.status-done {
    --status-color: #166534;
    --status-bg: #dcfce7;
    --status-border: #bbf7d0;
  }
  .status-badge.status-skipped,
  .status-option.status-skipped {
    --status-color: #6b21a8;
    --status-bg: #f3e8ff;
    --status-border: #e9d5ff;
  }
  .status-badge.status-not_applicable,
  .status-option.status-not_applicable {
    --status-color: #4b5563;
    --status-bg: #f3f4f6;
    --status-border: #e5e7eb;
  }

  .status-badge.status-unstarted,
  .status-badge.status-in_progress,
  .status-badge.status-done,
  .status-badge.status-skipped,
  .status-badge.status-not_applicable {
    color: var(--status-color);
    background: var(--status-bg);
    border-color: var(--status-border);
  }

  /* ─── Status dropdown menu (fixed positioned; placed via JS) ─── */
  .status-menu-popover {
    position: fixed;
    z-index: 60;
    min-width: 11rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    padding: 0.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
    animation: status-menu-in 0.12s ease-out;
  }

  @keyframes status-menu-in {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .status-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4375rem 0.625rem;
    border: none;
    background: transparent;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #374151;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
  }

  .status-option:hover {
    background: #f3f4f6;
  }

  .status-option.active {
    background: #f9fafb;
  }

  /* Tinted dot + text per status for the dropdown options */
  .status-option.status-unstarted,
  .status-option.status-in_progress,
  .status-option.status-done,
  .status-option.status-skipped,
  .status-option.status-not_applicable {
    color: var(--status-color);
    font-weight: 600;
  }

  .status-option.status-unstarted:hover,
  .status-option.status-in_progress:hover,
  .status-option.status-done:hover,
  .status-option.status-skipped:hover,
  .status-option.status-not_applicable:hover {
    background: var(--status-bg);
  }

  .status-option.status-unstarted.active,
  .status-option.status-in_progress.active,
  .status-option.status-done.active,
  .status-option.status-skipped.active,
  .status-option.status-not_applicable.active {
    background: var(--status-bg);
  }

  .status-check {
    margin-left: auto;
    font-size: 0.6875rem;
    color: #3b82f6;
    font-weight: 700;
  }

  .sheet-empty {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem 0.875rem;
    color: #6b7280;
    font-size: 0.75rem;
  }

  .sheet-empty .section-add {
    opacity: 1;
    color: #2563eb;
    background: white;
    border-color: #d1d5db;
  }

  .sheet-empty .section-add:hover {
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  /* ─── Category picker (item form dropdown) ─── */
  .page-intro-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .category-picker-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 0.625rem;
    color: #1f2937;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 400;
    text-align: left;
    transition: border-color 0.15s;
  }

  .category-picker-trigger:hover {
    border-color: #9ca3af;
  }

  .category-picker-trigger:focus-visible {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  }

  .category-menu-popover {
    position: fixed;
    z-index: 70;
    min-width: 14rem;
    max-width: 16rem;
    max-height: 18rem;
    overflow-y: auto;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    padding: 0.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
    animation: status-menu-in 0.12s ease-out;
  }

  .category-option,
  .category-add-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.4375rem 0.625rem;
    border: none;
    background: transparent;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #374151;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
  }

  .category-option:hover,
  .category-add-option:hover {
    background: #f3f4f6;
  }

  .category-option.active {
    background: #f9fafb;
  }

  .category-option .category-tag {
    pointer-events: none;
  }

  .category-add-option {
    color: #2563eb;
    font-weight: 600;
    border-top: 1px solid #f3f4f6;
    border-radius: 0;
    margin-top: 0.125rem;
  }

  .category-add-option:hover {
    background: #eff6ff;
  }

  .add-icon {
    font-size: 0.8125rem;
    font-weight: 700;
  }

  .custom-value {
    opacity: 0.85;
  }

  .custom-hint {
    font-size: 0.625rem;
    color: #9ca3af;
    font-weight: 600;
  }

  .category-new-form {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.375rem;
  }

  .category-new-form input {
    padding: 0.4375rem 0.625rem;
    color: #1f2937;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 400;
  }

  .category-new-form input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  }

  .category-swatches {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
  }

  .category-swatch {
    width: 1.375rem;
    height: 1.375rem;
    border-radius: 999px;
    border: 2px solid transparent;
    cursor: pointer;
    transition: transform 0.1s;
  }

  .category-swatch:hover {
    transform: scale(1.1);
  }

  .category-swatch.selected {
    border-color: #111827;
    box-shadow: inset 0 0 0 2px white;
  }

  .category-new-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.375rem;
  }

  /* ─── Manage categories modal ─── */
  .modal-subtext {
    margin: 0;
    color: #6b7280;
    font-size: 0.6875rem;
    line-height: 1.45;
  }

  .category-manage-list {
    display: grid;
    gap: 0.375rem;
  }

  .category-manage-row {
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    align-items: center;
    gap: 0.5rem;
  }

  .category-dot-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    cursor: pointer;
    transition: border-color 0.15s;
  }

  .category-dot-btn:hover,
  .category-dot-btn:focus-visible {
    outline: none;
    border-color: #3b82f6;
  }

  .category-dot {
    display: block;
    width: 0.875rem;
    height: 0.875rem;
    border-radius: 999px;
  }

  .category-dot.placeholder {
    background: transparent;
    border: 1.5px dashed #9ca3af;
    box-sizing: border-box;
  }

  .category-manage-row,
  .category-manage-add {
    column-gap: 0.5rem;
  }

  .category-manage-row input,
  .category-manage-add input {
    padding: 0.375rem 0.5rem;
    color: #1f2937;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 400;
  }

  .category-manage-row input:focus,
  .category-manage-add input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  }

  .manage-action-btn {
    min-width: 3.75rem;
    text-align: center;
  }

  .category-manage-add {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    padding-top: 0.625rem;
    border-top: 1px solid #e5e7eb;
  }

  /* App-style color tooltip (fixed-position, centered above the element) */
  .cat-tooltip {
    position: fixed;
    z-index: 200;
    transform: translate(-50%, -100%);
    padding: 0.25rem 0.5rem;
    background: #1f2937;
    color: white;
    border-radius: 0.375rem;
    font-size: 0.625rem;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    pointer-events: none;
    white-space: nowrap;
    animation: cat-tooltip-in 0.12s ease-out;
  }

  @keyframes cat-tooltip-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Manage categories: color picker popover */
  .color-picker-popover {
    min-width: 0;
    width: max-content;
    max-width: none;
    padding: 0.5rem;
  }

  .color-picker-popover .category-swatches {
    display: grid;
    grid-template-columns: repeat(5, auto);
    gap: 0.5rem;
  }

  /* ─── Forms ─── */
  .empty-state {
    padding: 1.5rem 1rem;
    text-align: center;
    background: white;
    border: 1px dashed #e5e7eb;
    border-radius: 0.5rem;
  }

  .empty-state h3 {
    margin: 0 0 0.5rem;
    color: #1f2937;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .empty-state p {
    margin: 0 0 0.75rem;
    color: #6b7280;
    font-size: 0.75rem;
  }

  /* ─── Library list ─── */
  .library-list {
    display: grid;
    gap: 1rem;
  }

  /* ─── History board (habits-style weekly grid) ─── */
  .history-toolbar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.625rem;
    padding: 0.625rem 0.75rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
  }

  .history-toolbar .archived-toggle {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-left: auto;
    color: #64748b;
    font-size: 0.8125rem;
  }

  .history-board-wrap {
    overflow: clip;
    background: white;
    border: 1px solid #e7eaf0;
    border-radius: 0.5rem;
  }

  .history-board-wrap .sticky-head {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .history-board-wrap .head {
    background: white;
    border-bottom: 1px solid #e7eaf0;
  }

  .grid {
    display: grid;
    grid-template-columns: minmax(200px, 260px) repeat(var(--week-columns, 12), minmax(55px, 1fr));
  }

  .item-head,
  .date {
    padding: 0.6875rem 0.75rem;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .date {
    display: grid;
    gap: 2px;
    min-height: 2.5rem;
    text-align: center;
  }

  .date small {
    font-size: 0.625rem;
    font-weight: 400;
    letter-spacing: 0;
    text-transform: none;
  }

  .date.today,
  .cell.today {
    box-sizing: border-box;
    border-left: 2px solid #647ce8;
    border-right: 2px solid #647ce8;
  }

  .date.today {
    background: #f5f7ff;
  }

  .group {
    border-bottom: 1px solid #e7eaf0;
  }

  .group-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 3.25rem;
    padding: 0.625rem 0.875rem;
    background: color-mix(in srgb, var(--group-color) 9%, white);
    border-bottom: 1px solid color-mix(in srgb, var(--group-color) 18%, white);
  }

  .group-title h2 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    color: #172033;
    font-size: 0.9375rem;
  }

  .group-color-dot {
    width: 0.625rem;
    height: 0.625rem;
    flex: none;
    border-radius: 999px;
    background: var(--group-color);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--group-color) 16%, transparent);
  }

  .group-progress {
    background: #fafbfc;
    color: #64748b;
    font-size: 11px;
    border-bottom: 1px solid #f0f2f5;
  }

  .group-progress > div {
    min-height: 26px;
    padding: 6px;
    text-align: center;
  }

  .group-progress > div:first-child {
    padding-left: 14px;
    text-align: left;
  }

  .cadence-row {
    grid-column: 1 / -1;
    padding: 0.3125rem 0.875rem;
    background: #f8fafc;
    border-bottom: 1px solid #f0f2f5;
    color: #64748b;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .history-row {
    height: 3.75rem;
    min-height: 3.75rem;
    grid-auto-rows: 3.75rem;
    border-bottom: 1px solid #f0f2f5;
  }

  .item-name {
    position: relative;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    min-height: 0;
    padding: 0.5625rem 0.625rem 0.5625rem 0.875rem;
  }

  .item-title {
    display: -webkit-box;
    overflow: hidden;
    padding: 1px 3px;
    border: 0;
    border-radius: 4px;
    background: none;
    color: #172033;
    font: 600 14px inherit;
    line-height: 1.35;
    text-align: left;
    cursor: pointer;
    overflow-wrap: break-word;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .item-title:hover:not(:disabled),
  .item-title:focus-visible {
    background: #eef2ff;
    color: #3730a3;
    outline: none;
  }

  .item-title.static {
    cursor: default;
  }

  .cell {
    display: grid;
    place-items: center;
    min-height: 0;
    border-left: 1px solid #f0f2f5;
  }

  .cell button {
    position: relative;
    display: grid;
    place-items: center;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    border: 0;
    background: transparent;
    color: #334155;
    cursor: pointer;
  }

  .cell-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    display: block;
    width: min(80%, 40px);
    height: min(80%, 40px);
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2.5;
    transform: translate(-50%, -50%);
  }

  .cell-icon-check {
    color: color-mix(in srgb, var(--completed-color) 45%, white);
  }

  .cell-icon-missed {
    color: #a14a4a;
  }

  .cell button:hover:not(:disabled),
  .cell button:focus-visible {
    background: color-mix(in srgb, var(--cell-color) 18%, white);
    outline: none;
  }

  .cell button.completed {
    background: var(--completed-color);
    color: #fff;
  }

  .cell button.completed:hover:not(:disabled),
  .cell button.completed:focus-visible {
    background: color-mix(in srgb, var(--completed-color) 82%, #172033);
  }

  .cell button.missed {
    background: #f8eaea;
    color: #a14a4a;
  }

  /* Weeks without a snapshot: item not yet created or already archived. */
  .cell-blank {
    width: 100%;
    height: 100%;
    background: #f1f5f9;
  }

  /* Weeks a non-weekly period spans but that do not carry its status get a
     hatched shade so the single status-bearing week stands out. */
  .cell-blank.period-span {
    background: repeating-linear-gradient(45deg, #f8fafc, #f8fafc 4px, #e8edf3 4px, #e8edf3 8px);
  }

  .item-name .notes-popover {
    right: auto;
    left: 0.5rem;
  }

  /* App-style dark tooltip pill (same treatment as the habits color tooltip). */
  .history-tooltip {
    position: fixed;
    z-index: 200;
    transform: translate(-50%, -100%);
    padding: 0.25rem 0.5rem;
    background: #1f2937;
    color: white;
    border-radius: 0.375rem;
    font-size: 0.625rem;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    pointer-events: none;
    white-space: nowrap;
  }

  .history-tooltip.wrap {
    max-width: 240px;
    white-space: normal;
    text-align: center;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    .grid {
      grid-template-columns: minmax(180px, 2.2fr) repeat(
          var(--week-columns, 12),
          minmax(48px, 1fr)
        );
    }
  }

  @media (max-width: 767px) {
    /* One 44px ‹ label › Today row; Show archived is in the ⚙ sheet. */
    .history-toolbar {
      padding: 0;
      border-radius: 0.75rem;
    }

    .grid {
      grid-template-columns: minmax(128px, 1.9fr) repeat(
          var(--week-columns, 12),
          minmax(30px, 1fr)
        );
    }

    .item-head,
    .date {
      padding: 0.5rem 0.125rem;
    }

    .item-head {
      padding-left: 0.625rem;
      font-size: 0.625rem;
    }

    .date small {
      display: none;
    }

    .history-row {
      height: 3.25rem;
      min-height: 3.25rem;
      grid-auto-rows: 3.25rem;
    }

    .item-name {
      padding: 0.35rem 0.25rem 0.35rem 0.625rem;
    }

    .item-title {
      font-size: 0.8125rem;
    }
  }

  .library-cadence-group,
  .library-category-group {
    display: grid;
    gap: 0.5rem;
  }

  .library-cadence-group > h3,
  .library-category-group > h4 {
    margin: 0;
    color: #334155;
    font-size: 0.75rem;
    font-weight: 700;
  }

  .library-cadence-group {
    gap: 0.75rem;
    padding: 0.75rem 0;
    border-top: 1px solid #e5e7eb;
  }

  .library-cadence-group:first-of-type {
    padding-top: 0;
    border-top: 0;
  }

  .library-item {
    display: flex;
    gap: 0.75rem;
    padding: 0.625rem 0.75rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
  }

  .library-item {
    align-items: start;
  }

  .library-main {
    display: flex;
    flex: 1 1 auto;
    align-items: start;
    justify-content: flex-start;
    gap: 1.25rem;
    min-width: 0;
  }

  .library-copy {
    flex: 0 1 28rem;
    min-width: 0;
  }

  .library-action-list {
    flex: 0 1 16rem;
    width: min(16rem, 100%);
    min-width: 0;
    padding-left: 0.75rem;
    border-left: 1px solid #e5e7eb;
    text-align: left;
  }

  .library-action-list-label {
    display: block;
    margin-bottom: 0.25rem;
    color: #6b7280;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .library-action-list ul {
    display: grid;
    gap: 0.25rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .library-action-list li {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    color: #64748b;
    font-size: 0.6875rem;
    line-height: 1.35;
  }

  .library-action-list input {
    width: 0.75rem;
    height: 0.75rem;
    margin: 0.0625rem 0 0;
    accent-color: #94a3b8;
    opacity: 0.7;
  }

  .library-item .library-actions {
    flex: 0 0 auto;
    align-self: center;
    margin-left: auto;
  }

  .library-copy h3 {
    margin: 0.25rem 0;
    color: #1f2937;
    font-size: 0.8125rem;
    font-weight: 600;
  }

  .library-copy p {
    margin: 0 0 0.375rem;
    color: #6b7280;
    font-size: 0.6875rem;
    line-height: 1.45;
    white-space: pre-line;
  }

  .library-meta {
    justify-content: flex-start;
    gap: 0.375rem;
  }

  .library-actions {
    gap: 0.25rem;
  }

  .pill {
    padding: 0.0625rem 0.375rem;
    color: #2563eb;
    background: #eff6ff;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .estimate {
    color: #6b7280;
    font-size: 0.6875rem;
  }

  .archived {
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid #e5e7eb;
  }

  .archived h3 {
    margin: 0 0 0.375rem;
    color: #4b5563;
    font-size: 0.75rem;
  }

  .archived > div {
    justify-content: space-between;
    padding: 0.375rem 0;
    color: #6b7280;
    font-size: 0.6875rem;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }

  /* ─── Modal (item form) ─── */
  .modal-overlay.discard-confirmation {
    z-index: 60;
  }

  .discard-modal-card {
    max-width: 25rem;
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    animation: overlay-in 150ms ease-out both;
  }

  .modal-overlay.closing {
    animation: overlay-out 100ms ease-in both;
  }

  .modal-overlay.closing .modal-card {
    animation: card-out 100ms ease-in both;
  }

  .modal-card {
    background: white;
    border-radius: 0.75rem;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 32rem;
    margin: 0 1rem;
    overflow: hidden;
    animation: card-in 150ms cubic-bezier(0.16, 1, 0.3, 1) both;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 2rem);
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .modal-header h2 {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #111827;
  }

  .modal-close {
    background: transparent;
    border: none;
    color: #9ca3af;
    padding: 0.25rem;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.12s;
  }

  .modal-close:hover {
    color: #4b5563;
    background: #f3f4f6;
  }

  .modal-body {
    padding: 1.125rem 1.25rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    overflow-y: auto;
  }

  .modal-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }

  .modal-field {
    display: grid;
    gap: 0.3125rem;
    color: #374151;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .modal-field input,
  .modal-field select,
  .modal-field textarea {
    padding: 0.5rem 0.625rem;
    color: #1f2937;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 400;
    transition: border-color 0.15s;
  }

  .modal-field input:focus,
  .modal-field select:focus,
  .modal-field textarea:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  }

  .modal-field textarea {
    resize: vertical;
    line-height: 1.5;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.25rem;
  }

  @keyframes overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  @keyframes overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
  @keyframes card-in {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
  @keyframes card-out {
    from {
      transform: scale(1);
      opacity: 1;
    }
    to {
      transform: scale(0.95);
      opacity: 0;
    }
  }

  /* ─── Responsive ─── */
  @media (max-width: 1100px) {
    .sheet-row {
      grid-template-columns:
        minmax(180px, 2fr) minmax(150px, 1.25fr) minmax(70px, 0.55fr) minmax(70px, 0.55fr)
        minmax(145px, 1fr) minmax(140px, 1.05fr);
    }
  }

  /* iPad portrait: retain page-level desktop controls, but prioritize each occurrence like mobile. */
  @media (max-width: 1023px) {
    .sheet-column-header {
      display: none;
    }

    .sheet-row.occurrence {
      display: grid;
      grid-template-columns: minmax(0, 3fr) minmax(12rem, 2fr);
      gap: 0;
      padding: 0;
    }

    .sheet-cell {
      display: block;
      padding: 0.625rem;
      border-right: 0;
      border-bottom: 1px solid #f3f4f6;
    }

    .sheet-cell::before,
    .checklist-cell::before,
    .actions-cell::before,
    .status-cell::before {
      display: none;
    }

    .checklist-cell {
      grid-column: 1;
      grid-row: 1;
      align-self: start;
      padding-bottom: 0.625rem;
    }

    .checklist-cell h4 {
      font-size: 1rem;
    }

    .checklist-cell .checklist-instructions {
      display: -webkit-box;
      overflow: hidden;
      margin-bottom: 0;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
    }

    .checklist-cell .checklist-instructions.expanded {
      display: block;
    }

    .instruction-toggle {
      padding: 0;
      color: #2563eb;
      background: transparent;
      border: 0;
      font-size: 0.6875rem;
      font-weight: 600;
      cursor: pointer;
    }

    .instruction-toggle:hover,
    .instruction-toggle:focus-visible {
      color: #1d4ed8;
      text-decoration: underline;
      outline: none;
    }

    .checklist-title {
      display: block;
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .actions-cell {
      grid-column: 2;
      grid-row: 1;
      align-self: start;
      margin-top: 2.875rem;
      padding-top: 0.375rem;
      padding-left: 0.875rem;
      border-left: 1px solid #f3f4f6;
    }

    .sheet-cell.status-cell {
      z-index: 1;
      display: flex;
      grid-column: 2;
      grid-row: 1;
      align-self: start;
      flex-wrap: nowrap;
      justify-content: flex-start;
      gap: 0.375rem;
      padding-block: 0.5rem;
      padding-left: 0.875rem;
      border-left: 1px solid #f3f4f6;
    }

    .sheet-row.occurrence .resource-cell,
    .sheet-row.occurrence .estimate-cell,
    .sheet-row.occurrence .notes-cell {
      display: none;
    }

    .compact-details {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.375rem;
      margin-top: 0.5rem;
    }

    .compact-occurrence-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem 0.625rem;
      min-width: 0;
      color: #6b7280;
      font-size: 0.6875rem;
    }

    .compact-occurrence-meta a {
      color: #2563eb;
      font-weight: 600;
      text-decoration: none;
    }

    .compact-occurrence-meta a:hover {
      color: #1d4ed8;
      text-decoration: underline;
    }

    .mobile-notes-link {
      flex: none;
      color: #2563eb;
      font: inherit;
      font-size: 0.75rem;
      font-weight: 600;
      text-decoration: none;
      background: transparent;
      border: 0;
      padding: 0;
      cursor: pointer;
    }

    .mobile-notes-link:hover,
    .mobile-notes-link:focus-visible {
      color: #1d4ed8;
      text-decoration: underline;
      outline: none;
    }

    .occurrence + .occurrence {
      border-top: 1px solid #e5e7eb;
    }
  }

  @media (max-width: 800px) {
    .library-main {
      flex-wrap: wrap;
    }

    .library-copy {
      flex: 1 1 100%;
    }

    .library-action-list {
      flex-basis: 100%;
      width: 100%;
      padding: 0.625rem 0 0;
      border-top: 1px solid #e5e7eb;
      border-left: 0;
    }
  }

  @media (max-width: 767px) {
    /* Keep cadence context on the first line and its controls on the second. */
    .cadence-header {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      gap: 0.5rem;
    }

    .cadence-heading h3 {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .period-pill {
      white-space: nowrap;
    }

    .cadence-header-actions {
      width: 100%;
    }

    .cadence-progress {
      flex: 1;
      min-width: 0;
    }

    .progress-track {
      flex: 1;
      min-width: 0;
      width: auto;
    }

    .checklist-cell h4 {
      font-size: 1.125rem;
    }

    /* The status column sizes to its badge + done button so they stay on one line. */
    .sheet-row.occurrence {
      grid-template-columns: minmax(0, 1fr) auto;
    }

    .checklist-cell {
      grid-column: 1 / -1;
      grid-row: auto;
      padding-right: 0.625rem;
      padding-bottom: 0.375rem;
    }

    .checklist-cell .checklist-instructions {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .checklist-cell .checklist-instructions.expanded {
      white-space: normal;
    }

    .actions-cell {
      grid-column: 1;
      grid-row: auto;
      margin-top: 0;
      padding-left: 0.625rem;
      border-left: 0;
    }

    .sheet-cell.status-cell {
      grid-column: 2;
      grid-row: auto;
      flex-wrap: nowrap;
      justify-content: flex-end;
      padding-left: 0.625rem;
      border-left: 0;
    }

    .status-badge {
      position: relative;
      min-height: 2.25rem;
      padding-inline: 0.625rem;
      font-size: 0.75rem;
    }

    .mark-done {
      position: relative;
      display: grid;
      width: 2.25rem;
      height: 2.25rem;
      place-items: center;
      padding: 0;
      border-radius: 999px;
    }

    .mark-done-label {
      display: none;
    }

    .mark-done-icon {
      display: block;
      width: 1rem;
      height: 1rem;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 2.5;
    }

    .compact-occurrence-meta {
      display: none;
    }

    .compact-details {
      display: block;
    }

    /* Tap targets (plan §3.7): the visuals stay, a transparent ::after grows the
       hit area to 44px. Where two neighbours' areas meet, the later one wins. */
    .mobile-notes-link,
    .instruction-toggle,
    .section-add,
    .status-badge,
    .mark-done {
      position: relative;
    }

    /* The title clips its overflow (ellipsis), which would clip an ::after, so it
       grows padding and gives the space back with a negative margin. */
    .checklist-title {
      padding-block: 10px;
      margin-block: -10px;
    }

    .mobile-notes-link::after,
    .instruction-toggle::after,
    .section-add::after,
    .status-badge::after,
    .mark-done::after {
      content: '';
      position: absolute;
    }

    .mobile-notes-link::after {
      inset: -13px -8px;
    }

    .instruction-toggle::after {
      inset: -15px -8px;
    }

    /* Offsets are from the padding box, so bordered controls need 1px more. */
    .section-add::after {
      inset: -11px -6px;
    }

    .status-badge::after,
    .mark-done::after {
      inset: -5px;
    }

    /* Checkbox rows are 44px tall, with the checkbox's label as the hit area. */
    .occurrence-actions {
      gap: 0;
    }

    .occurrence-actions li {
      min-height: var(--mobile-tap);
    }

    .action-check {
      display: flex;
      flex: none;
      align-items: center;
      justify-content: center;
      width: var(--mobile-tap);
      height: var(--mobile-tap);
      margin-inline: -15px;
    }

    .page-intro,
    .workspace-heading,
    .section-intro {
      align-items: stretch;
      flex-direction: column;
    }

    .page-intro .primary,
    .workspace-heading :global(.quiet) {
      align-self: flex-start;
    }

    .modal-row {
      grid-template-columns: 1fr;
    }

    .library-item {
      position: relative;
      flex-direction: column;
    }

    /* The card stacks, so ⋯ sits in its top-right corner instead of a row of its own */
    .library-menu-btn {
      position: absolute;
      top: 0.125rem;
      right: 0.375rem;
    }

    .library-item:has(.library-menu-btn) .library-copy {
      padding-right: 2rem;
    }

    .library-item {
      display: flex;
    }

    .library-action-list {
      min-width: 0;
    }

    .library-actions {
      justify-content: flex-end;
    }

    .page-body {
      padding: 0.75rem 0.875rem 1.5rem;
    }

    .content-container {
      gap: 0.75rem;
    }

    /* Library cards: no empty bands between the copy, the actions and the edges. */
    .library-view.card,
    .history-view.card {
      padding: 0.75rem;
    }

    .library-item {
      align-items: stretch;
      gap: 0;
      padding: 0.5rem 0.75rem;
    }

    .library-main {
      gap: 0.375rem;
    }

    .library-meta:empty {
      display: none;
    }

    .library-copy p {
      margin-bottom: 0;
    }

    .library-action-list {
      width: 100%;
      padding-top: 0.375rem;
    }
  }
</style>
